/**
 * Source des textes de la console (français = langue de référence).
 * en.ts doit avoir exactement les mêmes clés (vérifié par le typage et par i18n.test.ts).
 */
import { aiFr } from './ai.fr';
import { adminFr } from './admin.fr';
import { secretStoreFr } from './secret-store.fr';
import { billingFr } from './billing.fr';
import { catalogTableFr, glossaryFr } from './catalogue.fr';
import { catalogSearchFr, connectionsFr, lineageFr, previewFr, qualityFr } from './donnees.fr';
import { galleryFr, socialFr } from './social.fr';
import { searchFr } from './search.fr';
import { resourceUsageFr } from './usage.fr';
import { filePreviewFr } from './apercu-fichier.fr';
import { orchestrationFr } from './orchestration.fr';
import { alertsFr } from './alerts.fr';
import { managedWarehouseFr } from './entrepots-geres.fr';
import {
  bucketManageFr,
  preferencesFr,
  projectMetricsFr,
  projectSettingsFr,
  warehouseManageFr,
} from './gestes.fr';

export const fr = {
  common: {
    appName: 'graal',
    search: 'Rechercher',
    cancel: 'Annuler',
    close: 'Fermer',
    copy: 'Copier',
    copied: 'Copié',
    copyFailed: 'Copie impossible : sélectionnez le texte à la main.',
    moreActions: 'Plus d’actions',
    open: 'Ouvrir',
    filter: 'Filtrer',
    refresh: 'Actualiser',
    download: 'Télécharger',
    loading: 'Chargement…',
    escape: 'Échap',
    never: 'Jamais',
    manual: 'Manuel',
    selectAll: 'Tout sélectionner',
    selectRow: 'Sélectionner la ligne',
  },
  errorBoundary: {
    title: 'Cet écran a rencontré une erreur',
    description:
      'Une erreur inattendue a interrompu l’affichage. Rechargez la page ; si elle revient, transmettez la référence ci-dessous au support.',
    retry: 'Réessayer',
    reload: 'Recharger la page',
    home: 'Retour à l’accueil',
  },
  patterns: {
    reference: {
      label: 'Référence :',
      copy: 'Copier la référence de l’erreur',
      copied: 'Référence copiée',
    },
    codes: {
      SECRET_MANAGED:
        'Cette clé appartient à un bucket géré par la plateforme : sa valeur ne se remplace pas ici.',
      SECRET_INVALID: 'Le secret est invalide : vérifiez la valeur saisie.',
      INFRASTRUCTURE_NOT_TRUSTED:
        'Infrastructure non fiable (sa cible Kubernetes a été posée par un administrateur de locataire) : ce job a besoin d’un secret que graal ne remet pas à ce cluster — identité du job, bibliothèque en fichier, low-code, image privée… Lancez-le sur une infrastructure de la plateforme.',
      INFRASTRUCTURE_WORKLOAD_UNSUPPORTED:
        'Cette infrastructure n’accepte ni espace de travail, ni endpoint, ni stream, ni entrepôt : elle n’est pas fiable ou elle a son propre namespace. Choisissez une infrastructure de la plateforme.',
      SECRET_STORE_UNAVAILABLE:
        'Le coffre externe de l’organisation est injoignable : la valeur du secret ne peut être ni lue ni écrite. Réessayez plus tard.',
      SECRET_STORE_DENIED:
        'Le coffre externe de l’organisation refuse l’accès : un administrateur doit vérifier ses identifiants (Coffre des secrets).',
      SECRET_STORE_VALUE_MISSING:
        'La valeur de ce secret n’existe plus dans le coffre externe : ressaisissez-la.',
      SECRET_STORE_NOT_CONFIGURED:
        'Ce secret vit dans un coffre externe qui n’est plus désigné : un administrateur doit le reconfigurer (Coffre des secrets).',
    },
    states: {
      empty: {
        title: 'Rien à afficher pour l’instant',
        description: 'Cette liste est vide. Créez un premier élément pour la remplir.',
      },
      'no-results': {
        title: 'Aucun résultat',
        description: 'Aucun élément ne correspond à la recherche ou aux filtres actifs.',
      },
      forbidden: {
        title: 'Accès refusé',
        description:
          'Vos droits ne couvrent pas cette ressource. Demandez-les à un administrateur de l’organisation.',
      },
      'not-found': {
        title: 'Ressource introuvable',
        description: 'Elle a peut-être été supprimée, ou l’identifiant est erroné.',
      },
      unavailable: {
        title: 'Fonction indisponible',
        description:
          'Cette fonction n’est pas activée sur cette installation. Le reste de l’écran fonctionne normalement.',
      },
      busy: {
        title: 'Base momentanément occupée',
        description: 'La base de données est momentanément occupée. Réessayez dans un instant.',
        retryIn_one: 'La base de données est momentanément occupée. Réessayez dans {{count}} seconde.',
        retryIn_other: 'La base de données est momentanément occupée. Réessayez dans {{count}} secondes.',
      },
      error: {
        title: 'Une erreur est survenue',
        description: 'La demande n’a pas abouti. Réessayez dans un instant.',
      },
    },
    permissions: {
      denied: 'Vous n’avez pas le droit d’effectuer cette action.',
      checking: 'Vérification de vos droits…',
    },
    list: {
      searchPlaceholder: 'Rechercher…',
      allOf: 'Tous : {{name}}',
      clearFilters: 'Effacer les filtres',
      range: '{{from}}–{{to}} sur {{total}}',
      previousPage: 'Page précédente',
      nextPage: 'Page suivante',
      sort: {
        none: 'Trier',
        asc: 'Tri croissant',
        desc: 'Tri décroissant',
      },
    },
    detail: {
      back: 'Retour',
    },
    form: {
      save: 'Enregistrer',
      create: 'Créer',
      cancel: 'Annuler',
      optional: 'facultatif',
      importFile: 'Importer un fichier',
      choose: 'Choisir…',
      noChange: 'Aucune modification à enregistrer.',
      addPair: 'Ajouter une entrée',
      removePair: 'Retirer l’entrée',
      pairKey: 'Clé',
      pairValue: 'Valeur',
      addItem: 'Ajouter',
      removeItem: 'Retirer',
      errors: {
        required: 'Ce champ est obligatoire.',
        invalid: 'Valeur invalide.',
        invalidName: 'Lettres minuscules, chiffres et tirets uniquement.',
        invalidCron: 'Expression cron invalide.',
        tooLong: 'Valeur trop longue.',
        positive: 'Saisissez un nombre positif.',
        fileTooLarge: 'Fichier trop volumineux (1 Mio au plus).',
        fileUnreadable: 'Fichier illisible.',
      },
      cron: {
        count: 'Une expression cron compte 5 champs (min h jour mois jour-semaine) ou 6 avec les secondes.',
        field: 'Champ « {{field}} » : {{reason}}',
        range: 'valeur hors bornes',
        syntax: 'syntaxe non reconnue',
        fields: {
          second: 'secondes',
          minute: 'minutes',
          hour: 'heures',
          dayOfMonth: 'jour du mois',
          month: 'mois',
          dayOfWeek: 'jour de la semaine',
        },
        presets: {
          dailySix: 'Tous les jours à 6 h',
          hourly: 'Toutes les heures',
          weekly: 'Chaque lundi à 6 h',
        },
      },
    },
    wizard: {
      steps: 'Étapes',
      previous: 'Précédent',
      next: 'Suivant',
      finish: 'Terminer',
      position: 'Étape {{current}} sur {{total}}',
    },
  },
  nav: {
    label: 'Navigation principale',
    collapse: 'Réduire la barre latérale',
    /** Libellé VISIBLE (le nom accessible reste `collapse`) : en Geist Mono, la phrase entière dépassait 240 px. */
    collapseShort: 'Réduire',
    expand: 'Déplier la barre latérale',
    groups: {
      home: 'Accueil',
      projects: 'Projets',
      data: 'Données',
      workspaces: 'Espaces de travail',
      ml: 'ML',
      execution: 'Exécution',
      settings: 'Paramètres',
      account: 'Consommation et support',
      billing: 'Facturation',
      ai: 'IA et agents',
    },
    items: {
      overview: 'Vue d’ensemble',
      activity: 'Activité',
      projects: 'Tous les projets',
      jobs: 'Jobs',
      workflows: 'Workflows',
      triggers: 'Déclencheurs',
      editor: 'Éditeur low-code',
      libraries: 'Librairies',
      buckets: 'Buckets',
      catalog: 'Catalogue',
      glossary: 'Glossaire',
      connections: 'Connexions de données',
      lineage: 'Lignage',
      sql: 'Entrepôt SQL',
      streams: 'Streams',
      jupyter: 'Jupyter',
      vscode: 'VS Code',
      rstudio: 'RStudio',
      sessions: 'Sessions',
      experiments: 'Expériences',
      models: 'Modèles',
      endpoints: 'Endpoints',
      infrastructures: 'Infrastructures',
      instanceTypes: 'Types d’instance',
      runtimes: 'Runtimes',
      devices: 'Appareils',
      bridges: 'Bridges',
      members: 'Membres',
      roles: 'Rôles et affectations',
      tokens: 'Jetons et agents',
      secrets: 'Secrets',
      secretStore: 'Coffre des secrets',
      firewall: 'Pare-feu',
      auditTrail: 'Journal d’audit',
      branding: 'Marque blanche',
      mfa: 'Double facteur',
      audit: 'Connexions',
      usage: 'Usage et coûts',
      quotas: 'Quotas et capacité',
      tickets: 'Support',
      invoices: 'Factures',
      transactions: 'Transactions',
      paymentMethods: 'Moyens de paiement',
      subscription: 'Abonnement',
      pricing: 'Tarifs',
      llmGateway: 'Passerelle LLM',
      llmKeys: 'Clés de la passerelle',
      assistant: 'Assistant documentaire',
      codeAssistant: 'Assistant de code',
      agents: 'Agents',
      agentPolicies: 'Politiques d’agents',
    },
  },
  topbar: {
    breadcrumb: 'Fil d’Ariane',
    search: 'Rechercher…',
    agents: 'Agents',
    agentsConnected_one: '{{count}} agent connecté',
    agentsConnected_other: '{{count}} agents connectés',
    notifications: 'Notifications',
    unread_one: '{{count}} non lue',
    unread_other: '{{count}} non lues',
    markAllRead: 'Tout marquer comme lu',
    viewAll: 'Tout voir',
    tenant: 'Organisation',
    switchTenant: 'Changer d’organisation',
    project: 'Projet',
    switchProject: 'Changer de projet',
    allProjects: 'Tous les projets',
    manageTenants: 'Gérer les organisations',
    userMenu: 'Menu utilisateur',
    profile: 'Profil',
    following: 'Abonnements',
    preferences: 'Préférences',
    theme: 'Thème',
    language: 'Langue',
    documentation: 'Documentation',
    apiDocs: 'Documentation de l’API',
    thirdPartyNotices: 'Licences tierces',
    signOut: 'Se déconnecter',
    dark: 'Sombre',
    light: 'Clair',
    system: 'Système',
  },
  status: {
    queued: 'En file',
    pending: 'En attente',
    running: 'En cours',
    succeeded: 'Réussi',
    failed: 'Échec',
    cancelled: 'Annulé',
    retrying: 'Nouvelle tentative',
    skipped: 'Ignoré',
  },
  jobTypes: {
    python: 'Python',
    bash: 'Bash',
    spark: 'Spark',
    lowcode: 'Low-code',
    docker: 'Docker',
    dbt: 'dbt',
    dask: 'Dask',
    ray: 'Ray',
    flink: 'Flink',
    tensorflow: 'TensorFlow',
    pytorch: 'PyTorch',
    mxnet: 'MXNet',
    xgboost: 'XGBoost distribué',
    notebook: 'Notebook',
    knime: 'KNIME',
    'database-migration': 'Migration de base (Liquibase)',
    hive: 'Hive (HiveQL)',
    apachesqoop: 'Sqoop',
    apachehadoop: 'Hadoop',
    apachemapreduce: 'MapReduce',
    beam: 'Apache Beam',
    'spring-batch': 'Spring Batch',
    'spring-cloud-data-flow': 'Spring Cloud Data Flow',
    'akka-data-pipeline': 'Pipeline Pekko (ex-Akka)',
    pulsar: 'Pulsar',
    airflow: 'Airflow',
    sql: 'SQL',
    sas: 'SAS',
  },
  agent: {
    badge: 'Agent',
    createdBy: 'Créé par un agent IA via MCP',
    via: 'via {{client}}',
  },
  projects: {
    title: 'Projets',
    subtitle: 'Les projets de l’organisation {{tenant}}.',
    new: 'Nouveau projet',
    newTitle: 'Nouveau projet',
    newSubtitle: 'Un projet regroupe des jobs, des workflows et leurs données.',
    searchPlaceholder: 'Rechercher un projet…',
    columns: {
      name: 'Nom',
      description: 'Description',
      badge: 'Repère',
      labels: 'Étiquettes',
      owner: 'Propriétaire',
      updated: 'Modifié',
    },
    fields: {
      name: 'Nom',
      nameHelp: 'Minuscules, chiffres et tirets — sert d’identifiant dans les URL.',
      description: 'Description',
      badge: 'Repère visuel',
      badgeHelp: 'Couleur du projet dans la navigation.',
      labels: 'Étiquettes',
    },
    badges: {
      primary: 'Marque',
      info: 'Information',
      success: 'Réussite',
      warning: 'Attention',
      danger: 'Critique',
      neutral: 'Neutre',
    },
    empty: {
      title: 'Aucun projet pour l’instant',
      description: 'Créez un premier projet pour y ranger vos jobs et vos workflows.',
    },
    created: 'Projet « {{name}} » créé.',
    openJobs: 'Voir les jobs',
    emptyTenant: {
      title: 'Aucun projet dans cette organisation',
      description:
        'Jobs, workflows et droits se rangent dans un projet. Créez le premier : la vue d’ensemble, les jobs et les workflows s’y ouvriront.',
    },
  },
  workflows: {
    title: 'Workflows',
    subtitle: 'Orchestration des jobs du projet {{project}}.',
    new: 'Nouveau workflow',
    newTitle: 'Nouveau workflow',
    searchPlaceholder: 'Rechercher un workflow…',
    columns: {
      name: 'Nom',
      schedule: 'Planification',
      tasks: 'Tâches',
      updated: 'Modifié',
      taskName: 'Étape',
      taskType: 'Type',
      taskDepends: 'Dépend de',
    },
    empty: {
      title: 'Aucun workflow dans ce projet',
      description: 'Un workflow enchaîne plusieurs jobs et porte leur planification.',
    },
    steps: {
      basics: 'Définition',
      basicsHelp: 'Nom et description du workflow.',
      tasks: 'Tâches',
      tasksHelp: 'Ce que le workflow enchaîne : des jobs du projet, ou un script de vérification.',
      schedule: 'Planification',
      scheduleHelp: 'Manuel, ou planifié par une expression cron validée avant l’envoi.',
      review: 'Récapitulatif',
      reviewHelp: 'Vérifiez avant de créer : rien n’est envoyé tant que vous n’avez pas terminé.',
    },
    fields: {
      name: 'Nom',
      description: 'Description',
      scheduleType: 'Déclenchement',
      scheduleOnce: 'Manuel',
      scheduleCron: 'Planifié (cron)',
      cronExpression: 'Expression cron',
      timezone: 'Fuseau horaire',
      labels: 'Étiquettes',
    },
    created: 'Workflow « {{name}} » créé.',
    tasks: {
      name: 'Nom de l’étape',
      kind: 'Nature',
      kinds: {
        job: 'Lancer un job',
        script: 'Script de vérification',
        workflow: 'Lancer un sous-workflow',
        condition: 'Condition (branchement)',
        time: 'Attendre',
        error: 'Faire échouer le workflow',
      },
      job: 'Job',
      after: 'Après',
      noDependency: 'Aucune (démarre le workflow)',
      untitled: 'Étape {{position}}',
      script: 'Script',
      scriptHelp: 'Une commande par ligne. Un code de sortie non nul fait échouer le workflow.',
      add: 'Ajouter une étape',
      remove: 'Retirer cette étape',
      atLeastOne: 'Un workflow enchaîne au moins une étape.',
      duplicateName: 'Deux étapes ne peuvent pas porter le même nom.',
      workflow: 'Workflow',
      workflowHelp:
        'Un workflow du même projet, lancé puis attendu jusqu’à sa fin. Il faut le droit de le lancer ; un cycle est refusé.',
      expression: 'Expression',
      expressionHelp:
        'Lit le statut (steps.<étape>.status : succeeded ou skipped) ou les résultats publiés (steps.<étape>.results.<clé>) des étapes en amont ; comparaisons == != < <= > >=, et ! && || et parenthèses. Vraie : la suite part. Fausse : l’étape et tout ce qui en dépend sont ignorés.',
      expressionUpstream: 'Étapes en amont : {{steps}}.',
      expressionNoUpstream: 'Cette étape n’a encore aucune étape en amont à citer.',
      waitMode: 'Attendre',
      waitModes: {
        duration: 'Une durée',
        until: 'Jusqu’à une date',
      },
      duration: 'Durée (secondes)',
      durationHelp: 'À compter du début de l’étape, sans aucun pod. Le délai global du workflow s’applique.',
      until: 'Date et heure',
      untilHelp: 'Au format ISO 8601 avec fuseau, par exemple 2026-10-20T06:00:00Z.',
      message: 'Message',
      messageHelp: 'La raison donnée à l’échec du workflow. Une étape d’erreur n’est jamais reprise.',
      tooLong: '{{max}} caractères au plus.',
      invalidUntil: 'Une date ISO 8601 avec fuseau est attendue (2026-10-20T06:00:00Z).',
      invalidDuration: 'Un nombre entier de secondes, entre 0 et {{max}}.',
      seconds_one: '{{count}} seconde',
      seconds_other: '{{count}} secondes',
    },
    manual: 'Manuel',
    taskCount_one: '{{count}} tâche',
    taskCount_other: '{{count}} tâches',
    runNow: 'Lancer maintenant',
    runStarted: 'Run {{name}} lancé.',
    runFailed: 'Le lancement du run a échoué.',
    delete: 'Supprimer le workflow',
    deleted: 'Workflow « {{name}} » supprimé.',
    deleteFailed: 'La suppression a échoué.',
    detail: {
      subtitle_one: 'Projet {{project}} · {{count}} étape.',
      subtitle_other: 'Projet {{project}} · {{count}} étapes.',
      tabs: {
        summary: 'Résumé',
        runs: 'Runs',
        notifications: 'Notifications',
        costs: 'Coûts',
        permissions: 'Permissions',
      },
      totalRuns: 'Runs',
      succeeded: 'Réussis',
      failed: 'En échec',
      running: 'En cours',
      lastRun: 'Dernier run',
      noRuns: 'Aucun run pour l’instant',
      runNumber: 'Run',
      updated: 'Modifié le',
      identity: 'Identité d’exécution',
      tasks: 'Étapes',
      tasksHint: 'Les étapes se définissent à la création du workflow (étape « Tâches » de l’assistant).',
      noTasks: 'Aucune étape dans ce workflow',
      saved: 'Workflow enregistré.',
      notificationsOnStart: 'Au démarrage',
      notificationsOnSuccess: 'En cas de réussite',
      notificationsOnFailure: 'En cas d’échec',
      noNotifications: 'Aucune notification configurée sur ce workflow.',
      permissionsHint:
        'Les rôles accordés sur ce workflow décident qui peut le lire, le lancer et le modifier.',
      grantRoles: 'Gérer les rôles',
    },
    run: {
      title: 'Run {{name}}',
      subtitle: 'Exécution du workflow {{workflow}}.',
      tabs: {
        summary: 'Résumé',
        events: 'Événements',
      },
      runId: 'Identifiant du run',
      workflow: 'Workflow',
      steps: 'Étapes',
      stepsHint: 'Statut déduit des événements : le contrat n’expose pas de statut par étape.',
      timeline: 'Chronologie',
      timelineLabel: 'Chronologie des événements du run',
      noSteps: 'Ce workflow n’a aucune étape à suivre',
      noEvents: 'Aucun événement pour l’instant',
      noEventsHelp: 'Le run vient d’être créé : les événements arrivent au fil de l’exécution.',
      searchPlaceholder: 'Rechercher un événement…',
      wholeRun: 'run entier',
      liveLabel: 'Suivi',
      liveOn: 'en direct (5 s)',
      liveOff: 'terminé',
      kill: 'Arrêter',
      killed: 'Arrêt du run demandé.',
      killFailed: 'L’arrêt du run a échoué.',
      delete: 'Supprimer le run',
      deleted: 'Run supprimé.',
      deleteFailed: 'La suppression a échoué.',
      openChildRun: 'Ouvrir le run',
      columns: {
        at: 'Heure',
        type: 'Événement',
        step: 'Étape',
        childRun: 'Run enfant',
      },
      types: {
        prestart: 'Préparation',
        started: 'Démarré',
        prestop: 'Arrêt en cours',
        complete: 'Terminé',
        failed: 'En échec',
      },
    },
  },
  jobs: {
    title: 'Jobs',
    subtitle:
      'Les traitements du projet {{project}} : scripts, Spark, low-code, calcul distribué, apprentissage.',
    new: 'Nouveau job',
    filterPlaceholder: 'Filtrer les jobs…',
    fields: {
      name: 'Nom',
      description: 'Description',
      labels: 'Étiquettes',
      env: 'Variables d’environnement',
      parameters: 'Paramètres positionnels',
      module: 'Module',
      moduleHelp: 'Nom du module Python à exécuter.',
      lines: 'Script',
      linesHelp: 'Une commande par ligne, exécutée par bash.',
      mainLibrary: 'Bibliothèque principale',
      mainLibraryHelp: 'Fichier jar ou py déposé dans les bibliothèques du projet.',
      mainClassName: 'Classe principale',
      pyFiles: 'Fichiers Python additionnels',
      conf: 'Configuration Spark (--conf)',
      definition: 'Graphe low-code',
      definitionHelp: 'Graphe construit dans l’éditeur low-code.',
      mode: 'Mode d’exécution',
      instanceType: 'Type d’instance',
      driverInstanceType: 'Type d’instance du driver',
      executorInstanceType: 'Type d’instance des exécuteurs',
      numberExecutors: 'Nombre d’exécuteurs',
      scheduleType: 'Déclenchement',
      scheduleOnce: 'Manuel',
      scheduleCron: 'Planifié (cron)',
      cronExpression: 'Expression cron',
      timeoutSeconds: 'Délai maximum (s)',
      maxRetries: 'Tentatives supplémentaires',
      secrets: 'Secrets',
      infrastructureId: 'Infrastructure d’exécution',
      infrastructureIdHelp: 'Un job planifié crée ses runs seul : ils partent sur cette infrastructure.',
      identityId: 'Identité d’exécution',
      identityIdHelp:
        'Compte machine sous lequel le job s’exécute : c’est lui qui tire les fichiers et les secrets du job. Obligatoire dès que le job cite un secret.',
      libraries: 'Bibliothèques',
      librariesHelp:
        'Fichiers déposés dans Bibliothèques, copiés dans le répertoire de travail du run (/workspace) : code, jar, données, programmes SAS appelés par %INCLUDE.',
      notebook: 'Notebook (chemin)',
      notebookHelp:
        'Chemin d’un .ipynb dans les fichiers du job. Ou bien collez le notebook ci-dessous — l’un des deux.',
      notebookSource: 'Notebook (contenu)',
      notebookSourceHelp: 'Le .ipynb lui-même (JSON nbformat 4, 512 Kio au plus).',
      notebookParameters: 'Paramètres',
      notebookParametersHelp:
        'Un objet JSON, injecté après la cellule taguée « parameters » (comme papermill).',
      kernel: 'Noyau',
      cellTimeoutSeconds: 'Délai par cellule (s)',
      experiment: 'Expérience MLflow',
      hiveQuery: 'Requête HiveQL',
      hiveQueryHelp: 'Exécutée par spark-sql sur le catalogue Iceberg ; une instruction par ligne.',
      sasProgram: 'Programme SAS',
      sasProgramHelp:
        'Exécuté par graal-sas, sans licence SAS : étape DATA, PROC courantes, macros, LIBNAME vers Iceberg, S3 ou PostgreSQL. Facultatif avec un programme principal : il s’exécute alors AVANT lui (%LET, OPTIONS, LIBNAME). Les fichiers isolés (.xpt, .sas7bdat, CSV) se choisissent dans Bibliothèques, à l’étape Avancé ; un chemin relatif les désigne. Une construction hors de la liste publiée fait échouer le run avec une ERROR nommée (code 3), jamais en silence.',
      sasMainProgram: 'Programme principal',
      sasMainProgramHelp:
        'Chemin du programme, relatif, terminé par .sas : dans le projet (archive ou dépôt), qui devient le répertoire de travail, ou, sans projet, parmi les fichiers de Bibliothèques copiés dans /workspace. %INCLUDE, LIBNAME et autocall s’y résolvent.',
      sasArchive: 'Archive du projet',
      sasArchiveHelp: 'Un .zip déposé dans Bibliothèques, extrait dans le run. Exclut le dépôt Git.',
      sasRepositoryUrl: 'Dépôt Git du projet',
      sasRepositoryUrlHelp: 'URL https seulement, cloné au début du run. Exclut l’archive.',
      sasRepositoryRevision: 'Révision (branche, tag ou commit)',
      sasRepositoryPath: 'Sous-dossier du projet',
      sasRepositoryUsername: 'Utilisateur Git',
      sasRepositoryPassword: 'Mot de passe ou jeton Git',
      sasRepositoryPasswordHelp:
        'Scellé par graal à l’enregistrement et jamais réaffiché ; il arrive au run par son Secret. Une copie de job ne le reprend pas : à ressaisir.',
      sasOutput: 'Publication des sorties',
      sasOutputHelp:
        's3://<bucket du projet>/rapports : listing, rapports ODS et exports y sont copiés sous <nom du job>/<id du run>. Le bucket porte le projet, et l’identité du job le lit et l’écrit.',
      none: 'Aucune',
      command: 'Commande',
      dbtCommandHelp: 'Commande dbt complète, exécutée dans le projet dbt.',
      sqoopCommandHelp:
        'La ligne Sqoop sans le mot « sqoop » : import, export, eval, list-tables ou list-databases. Aucun mot de passe sur la ligne. Avec une connexion du projet, ni --connect ni --username (ex. import --table conso --hive-import --hive-table demo.conso) ; sans connexion, --connect jdbc:… est requis.',
      sqoopCommandPlaceholder: 'import --table conso --target-dir s3a://<bucket>/sqoop/conso',
      sqoopConnection: 'Connexion du projet',
      sqoopConnectionHelp:
        'Une base de données du projet (PostgreSQL, MySQL, MariaDB, SQL Server ou Oracle). Choisie, elle fournit --connect, --username et le mot de passe.',
      sqoopNoConnection: 'Aucune — --connect jdbc:… sur la ligne',
      adapter: 'Adaptateur',
      target: 'Cible (target)',
      profile: 'Dossier des profils',
      profileHelp: 'Vide : le profil fourni par graal.',
      datawarehouseId: 'Entrepôt cible',
      datawarehouseIdHelp: 'Identifiant de l’entrepôt dont le schéma est migré.',
      repositoryUrl: 'Dépôt Git du changelog',
      repositoryPath: 'Chemin du changelog',
      repositoryRevision: 'Révision (branche, tag ou commit)',
      workflow: 'Workflow KNIME',
      knimeWorkflowHelp: 'Nom du workflow à exécuter dans l’archive.',
      archive: 'Archive du workflow',
      archiveHelp: 'Fichier .knwf déposé dans les bibliothèques du projet.',
      preferences: 'Préférences KNIME',
      dagId: 'Identifiant du DAG',
      packageName: 'Paquet Python',
      moduleName: 'Module',
      functionName: 'Fonction',
      numberWorkers: 'Nombre de workers',
      numberWorkersMax: 'Nombre maximum de workers',
      workerInstanceType: 'Type d’instance des workers',
      jobManagerInstanceType: 'Type d’instance du JobManager',
      taskManagerInstanceType: 'Type d’instance des TaskManagers',
      numberTaskManagers: 'Nombre de TaskManagers',
      runner: 'Exécuteur Beam',
      runnerHelp: 'direct : dans le pod ; flink : un cluster Flink standalone créé pour le run.',
      brokerInstanceType: 'Type d’instance du broker',
      numberBrokers: 'Nombre de brokers',
      numberReplicas: 'Nombre de réplicas',
      replicaInstanceType: 'Type d’instance des réplicas',
      masterInstanceType: 'Type d’instance du maître',
      schedulerInstanceType: 'Type d’instance de l’ordonnanceur',
      serverInstanceType: 'Type d’instance des serveurs',
      image: 'Image',
      imageHelp:
        'Image du projet, depuis un registre autorisé par l’installation. Elle tourne sous un UID imposé.',
      memberInstanceTypeHelp:
        'Taille de CHAQUE membre (workers, maître, ordonnanceur) : un seul gabarit de pod.',
      gpu: 'GPU',
      gpuHelp: 'Exige un type d’instance à cartes et une infrastructure qui déclare ses nœuds GPU.',
      statement: 'Requête SQL',
      statementHelp: 'Une seule instruction de lecture (SELECT, WITH, VALUES).',
      sqlDatawarehouseIdHelp: 'Identifiant de l’entrepôt interrogé ; il faut pouvoir le lire.',
      output: 'Sortie',
      table: 'Table cible',
      sqlTableHelp: 'Sortie « table » : une table Iceberg du schéma de l’entrepôt (minuscules, chiffres, _).',
      sqlMode: 'Si la table existe',
      format: 'Format du fichier',
      path: 'Dossier du lac',
      sqlPathHelp: 'Sortie « fichier » : dossier relatif du lac de données. Vide : sql-results/<job>/<run>.',
      port: 'Port exposé',
      properties: 'Propriétés de la JVM (-D)',
      numberTaskManagersHelp:
        '0 : Flink en local, dans le pod du run ; au-delà, un cluster standalone créé pour le run.',
      topics: 'Sujets Pulsar',
      topicsHelp:
        'Créés avant le programme ; nom court (public/default) ou persistent://locataire/espace/sujet.',
      passwordEnv: 'Secret du mot de passe JDBC',
      passwordEnvHelp:
        'Nom d’un secret du job : il n’apparaît jamais sur la ligne de commande. Sans connexion du projet seulement.',
      taskName: 'Nom de la tâche (spring.cloud.task.name)',
      taskNameHelp: 'Vide : le nom du job.',
      flinkConf: 'Configuration Flink (-D)',
    },
    typeHelp: {
      python: 'Un module Python, exécuté dans un pod.',
      bash: 'Un script shell, une commande par ligne.',
      spark: 'Spark en local[*], ou maître et workers créés pour le run.',
      lowcode: 'Le code généré depuis un graphe de l’éditeur, en Pandas ou PySpark.',
      docker: 'Une image du projet, depuis un registre autorisé.',
      dbt: 'Un projet dbt exécuté contre Trino.',
      dask: 'Un ordonnanceur Dask et ses workers, créés pour le run.',
      ray: 'Une tête Ray et ses workers, créés pour le run.',
      flink: 'Un cluster applicatif Flink autonome, le temps du run.',
      tensorflow: 'Entraînement distribué : N pods avec rendez-vous, GPU en option.',
      pytorch: 'Entraînement distribué (torchrun) : N pods avec rendez-vous, GPU en option.',
      mxnet: 'Entraînement distribué MXNet : ordonnanceur, serveurs et workers.',
      xgboost: 'XGBoost distribué : un maître et des workers.',
      notebook: 'Un notebook Jupyter exécuté de bout en bout ; le notebook exécuté est gardé dans MLflow.',
      knime: 'Un workflow KNIME en mode batch.',
      'database-migration': 'Un changelog Liquibase appliqué à un entrepôt.',
      hive: 'Du HiveQL exécuté par spark-sql sur le catalogue Iceberg.',
      apachesqoop: 'Un import ou un export Sqoop, entre JDBC et S3 ou Iceberg.',
      apachehadoop: 'Un « hadoop jar » en local, ou via l’émulateur YARN.',
      apachemapreduce: 'Un job MapReduce, en local ou via l’émulateur YARN.',
      beam: 'Un pipeline Beam : DirectRunner dans le pod, ou FlinkRunner sur un cluster créé pour le run.',
      'spring-batch': 'Une application Spring Batch dans un pod JVM.',
      'spring-cloud-data-flow': 'Une tâche Spring Cloud Data Flow dans un pod JVM.',
      'akka-data-pipeline': 'Un pipeline Apache Pekko (successeur d’Akka) dans un pod JVM.',
      pulsar: 'Un Pulsar autonome, géré par graal.',
      airflow: 'Un DAG Airflow exécuté dans un pod.',
      sql: 'Une requête de lecture sur un entrepôt, résultat en fichier du lac ou en table Iceberg.',
      sas: 'Un programme ou un projet SAS, exécuté sans licence SAS par graal-sas.',
    },
    wizard: {
      title: 'Nouveau job',
      subtitle: 'Projet {{project}}. Rien n’est créé tant que vous n’avez pas terminé.',
      types: 'Types de job',
      soon: 'bientôt',
      soonHint:
        '« {{type}} » n’est pas encore prouvé de bout en bout : l’API répondra « fonction indisponible » (501) à sa création.',
      groups: {
        essentials: 'Essentiels',
        data: 'Données',
        distributed: 'Calcul distribué',
        ml: 'Apprentissage',
        jvm: 'JVM et conteneurs',
      },
      steps: {
        type: 'Type',
        typeHelp:
          'Ce que le job exécute. Un type marqué « bientôt » est sélectionnable, mais pas encore prouvé.',
        basics: 'Définition',
        basicsHelp: 'Nom, description, étiquettes, variables d’environnement et paramètres.',
        options: 'Options',
        optionsHelp: 'Les champs propres au type choisi, et ses ressources.',
        schedule: 'Planification',
        scheduleHelp: 'Manuel, ou planifié par une expression cron validée avant l’envoi.',
        advanced: 'Avancé',
        advancedHelp: 'Délai, nouvelles tentatives, identité, secrets et bibliothèques.',
        review: 'Récapitulatif',
        reviewHelp: 'Vérifiez avant de créer : rien n’est envoyé tant que vous n’avez pas terminé.',
      },
      create: 'Créer le job',
      created: 'Job « {{name}} » créé.',
      prefilledFromGraph: 'Prérempli depuis le graphe « {{graph}} » de l’éditeur low-code.',
      duplicateOf: 'Copie du job « {{name}} » : choisissez un nouveau nom.',
      reenterGitPassword:
        'Le mot de passe du dépôt Git n’est pas repris (il est scellé pour le job d’origine) : à ressaisir à l’étape Options.',
      dagReady_one: 'DAG prêt pour le moteur low-code : {{count}} tâche.',
      dagReady_other: 'DAG prêt pour le moteur low-code : {{count}} tâches.',
      dagFrozen: 'Le DAG du graphe enregistré est recopié dans le job à sa création.',
      dagInvalid: 'Graphe inutilisable : nœud « {{node}} » — {{reason}}.',
      dagIncomplete: 'Graphe incomplet : {{problem}}.',
      graphUnknown: 'Graphe introuvable dans ce tenant.',
      sqoop: {
        connectionNotice:
          '--connect, --username et le mot de passe viennent de la connexion « {{name}} » : graal construit l’URL JDBC et remet le mot de passe au run par son secret, jamais en clair. La ligne ne les porte pas, et le secret du mot de passe reste vide.',
        lineHasConnection: 'Avec une connexion du projet, retirez --connect et --username de la ligne.',
        passwordEnvWithConnection:
          'Avec une connexion du projet, laissez ce champ vide : le mot de passe vient de la connexion.',
        missingConnect: 'Choisissez une connexion du projet, ou écrivez --connect jdbc:… sur la ligne.',
      },
      openEditor: 'Ouvrir l’éditeur low-code',
      openGraph: 'Ouvrir le graphe',
      alreadyExists: 'Un job porte déjà ce nom dans ce projet.',
      review: {
        type: 'Type',
        schedule: 'Planification',
        notSet: '—',
      },
    },
    detail: {
      subtitle: 'Job {{type}} du projet {{project}}.',
      tabs: {
        summary: 'Résumé',
        definition: 'Définition',
        runs: 'Runs',
        costs: 'Coûts',
        notifications: 'Notifications',
        permissions: 'Permissions',
        preview: 'Aperçu',
      },
      lastRun: 'Dernier run',
      totalRuns: 'Runs',
      succeeded: 'Réussis',
      failed: 'En échec',
      running: 'En cours',
      created: 'Créé le',
      updated: 'Modifié le',
      identity: 'Identité d’exécution',
      noRuns: 'Ce job n’a jamais été lancé.',
      noNotifications: 'Aucune notification configurée pour ce job.',
      notificationsOnStart: 'Au démarrage',
      notificationsOnSuccess: 'En cas de réussite',
      notificationsOnFailure: 'En cas d’échec',
      grantRoles: 'Gérer les rôles',
      permissionsHint: 'Les rôles rattachés à ce job décident de qui peut le lire, le lancer et le modifier.',
      saved: 'Job enregistré.',
      delete: 'Supprimer le job',
      runNumber: 'Run',
      notFound: 'Job introuvable.',
    },
    columns: {
      name: 'Nom',
      type: 'Type',
      status: 'Statut',
      lastRun: 'Dernier run',
      duration: 'Durée',
      schedule: 'Planification',
      owner: 'Propriétaire',
    },
    views: {
      all: 'Tous',
      running: 'En cours',
      failed: 'En échec',
      scheduled: 'Planifiés',
      agents: 'Créés par un agent',
    },
    pagination: '{{from}}–{{to}} sur {{total}}',
    previousPage: 'Page précédente',
    nextPage: 'Page suivante',
    empty: 'Aucun job ne correspond aux filtres.',
    rowActions: 'Actions du job',
    runWithInstanceType: 'Exécuter sur…',
    runNow: 'Lancer maintenant',
    edit: 'Modifier',
    duplicate: 'Dupliquer',
    viewRuns: 'Voir les runs',
    runStarted: 'Run {{name}} lancé.',
    runFailed: 'Le lancement du run a échoué.',
  },
  run: {
    outputs: {
      title: 'Fichiers produits',
      description: 'Ce que le run a publié (listing, rapports, exports), déclaré dans son résumé.',
      columns: { name: 'Nom', kind: 'Type', size: 'Taille' },
      download: 'Télécharger {{name}}',
      browse: 'Ouvrir le dossier de {{name}} dans le bucket',
      downloadFailed: 'Téléchargement impossible',
    },
    title: 'Run #{{number}}',
    tabs: {
      logs: 'Logs',
      events: 'Événements',
      metrics: 'Métriques',
      files: 'Fichiers',
      parameters: 'Paramètres',
      quality: 'Qualité',
      lineage: 'Lignage',
      report: 'Rapport',
    },
    report: {
      title: 'Rapport d’exécution',
      producer: 'Produit par {{producer}}',
      invocations: 'Commandes : {{list}}',
      invocation: '{{command}} (code {{code}})',
      truncated: 'Rapport tronqué : des étapes ont été retirées pour tenir dans la limite.',
      search: 'Rechercher une étape…',
      noSteps: 'Aucune étape déclarée.',
      empty: {
        title: 'Aucun rapport pour ce run',
        description:
          'Un run déclare son rapport en imprimant une ligne GRAAL_RUN_REPORT dans son journal. Le runtime dbt le fait seul ; une image personnalisée peut en faire autant.',
      },
      columns: {
        outcome: 'Issue',
        step: 'Étape',
        kind: 'Nature',
        invocation: 'Commande',
        duration: 'Durée',
        rows: 'Lignes',
        failures: 'En échec',
        message: 'Message',
      },
      outcomes: {
        success: 'Réussie',
        failure: 'Échec',
        warning: 'Avertissement',
        skipped: 'Ignorée',
        unknown: 'Inconnue',
      },
    },
    started: 'Démarré {{time}}',
    elapsed: 'Durée',
    triggeredBy: 'Déclenché par',
    image: 'Image',
    resources: 'Ressources',
    attempt: 'Tentative',
    details: 'Détails',
    runId: 'ID du run',
    job: 'Job',
    project: 'Projet',
    instanceType: 'Type d’instance',
    schedule: 'Planification',
    cancelRun: 'Annuler le run',
    cancelled: 'Annulation demandée : le run s’arrête.',
    cancelFailed: 'L’annulation du run a échoué.',
    rerun: 'Relancer',
    notFound: 'Run introuvable.',
    infrastructure: 'Infrastructure',
    initiator: 'Lancé par',
    copyAs: 'Copier en tant que…',
    copyAsCurl: 'Commande curl',
    copyAsMcp: 'Appel d’outil MCP',
    events: {
      columns: { at: 'Heure', type: 'Événement', message: 'Message' },
      search: 'Rechercher un événement…',
      empty: 'Aucun événement pour ce run.',
      emptyHelp: 'Le planificateur publie ici chaque étape du run : préparation, démarrage, fin.',
    },
    metrics: {
      names: {
        cpu: 'CPU (cœurs)',
        memory: 'Mémoire (Mio)',
        fsReads: 'Lectures disque',
        fsWrites: 'Écritures disque',
        netRx: 'Réseau entrant',
        netTx: 'Réseau sortant',
      },
      min: 'min. {{value}}',
      max: 'max. {{value}}',
      noPoints: 'Aucune mesure sur la durée du run.',
      summary: '{{metric}} : dernière valeur {{last}}, minimum {{min}}, maximum {{max}}.',
    },
    files: {
      description: 'Fichiers rattachés à ce run.',
      columns: { name: 'Fichier' },
      search: 'Rechercher un fichier…',
      download: 'Télécharger {{name}}',
      downloadFailed: 'Le téléchargement du fichier a échoué.',
      empty: 'Aucun fichier pour ce run.',
      emptyHelp: 'Un run qui écrit ses résultats les rend visibles ici.',
    },
    parameters: {
      title: 'Paramètres du run',
      description: 'Arguments positionnels transmis à cette exécution.',
      columns: { index: 'Position', value: 'Valeur' },
      empty: 'Ce run n’a reçu aucun paramètre.',
      input: 'Entrée (JSON)',
    },
    copiedAs: '{{format}} copié dans le presse-papiers',
    launch: {
      instanceType: 'Type d’instance du run',
      instanceTypeHelp:
        'Facultatif : remplace, pour ce run seulement, le type d’instance du job et de ses membres.',
      jobInstanceType: 'Celui du job',
      title: 'Choisir l’infrastructure d’exécution',
      description:
        'Cette organisation dispose de plusieurs infrastructures. Le run s’exécutera sur celle que vous choisissez.',
      field: 'Infrastructure',
      submit: 'Lancer',
      none: 'Aucune infrastructure d’exécution',
      noneHelp:
        'Un run s’exécute sur une infrastructure, et cette organisation n’en expose aucune. Demandez à un administrateur d’en déclarer une.',
      loadFailed: 'Les infrastructures de l’organisation sont illisibles : le run n’a pas été lancé.',
    },
  },
  logs: {
    search: 'Rechercher dans les logs',
    matches: '{{current}} / {{total}}',
    noMatch: 'Aucun résultat',
    previousMatch: 'Résultat précédent',
    nextMatch: 'Résultat suivant',
    follow: 'Suivre',
    wrap: 'Retour à la ligne',
    download: 'Télécharger les logs',
    downloadFailed: 'Le téléchargement du journal a échoué : {{detail}}',
    lines_one: '{{count, number}} ligne',
    lines_other: '{{count, number}} lignes',
    newLines_one: '{{count}} nouvelle ligne',
    newLines_other: '{{count}} nouvelles lignes',
    live: 'En direct',
    region: 'Journal du run',
    source: 'Source',
    allSources_one: 'Toutes les sources ({{count}})',
    allSources_other: 'Toutes les sources ({{count}})',
    mode: {
      polling: 'Journal relu toutes les 5 s (le flux en direct n’est pas disponible)',
      ended: 'Run terminé : journal complet',
    },
    dropped_one: '{{count, number}} ligne ancienne retirée de l’écran — le téléchargement garde tout',
    dropped_other: '{{count, number}} lignes anciennes retirées de l’écran — le téléchargement garde tout',
  },
  apiDocs: {
    title: 'Documentation de l’API',
    subtitle:
      'Le contrat REST de graal, servi par cette installation. « Try it out » agit avec vos droits, dans cette organisation.',
    frameTitle: 'Documentation interactive de l’API',
    unavailable:
      'La documentation interactive est servie par graal-api : elle n’existe pas en mode maquette.',
  },
  palette: {
    title: 'Palette de commandes',
    placeholder: 'Rechercher une ressource ou lancer une commande…',
    empty: 'Aucun résultat.',
    groups: {
      suggestions: 'Suggestions',
      navigation: 'Aller à',
      recent: 'Récents',
      projects: 'Projets',
      agents: 'Agents IA',
      appearance: 'Apparence',
      help: 'Aide',
    },
    apiDocs: 'Documentation de l’API',
    newJob: 'Créer un job',
    newProject: 'Créer un projet',
    newWorkflow: 'Créer un workflow dans ce projet',
    runJob: 'Lancer « {{name}} »',
    openEditor: 'Ouvrir l’éditeur low-code',
    connectAgent: 'Connecter un agent (MCP)',
    agentActivity: 'Voir l’activité des agents',
    searchEverywhere: 'Rechercher « {{query}} » partout',
    modes: {
      dark: 'Thème sombre',
      light: 'Thème clair',
      system: 'Thème du système',
    },
    current: '(actuel)',
    navigate: 'naviguer',
    select: 'ouvrir',
    dismiss: 'fermer',
  },
  agents: {
    title: 'Agents',
    subtitle: 'Agents IA connectés à ce tenant via MCP',
    connectedSection: 'Agents connectés',
    feedSection: 'Activité en direct',
    connectSection: 'Connecter un agent',
    live: 'En direct',
    lastSeen: 'actif {{time}}',
    scopes: 'Portées',
    scopeRead: 'lecture',
    scopeWrite: 'écriture',
    revoke: 'Révoquer',
    token: 'Jeton',
    tools: {
      create_job: 'a créé le job',
      run_job: 'a lancé',
      get_run_logs: 'a lu les logs de',
      schedule_job: 'a planifié',
      list_jobs: 'a listé les jobs de',
      get_costs: 'a consulté les coûts de',
      upload_code: 'a déposé du code dans',
    },
    connect: {
      claudeCode: 'Claude Code',
      anyClient: 'Autre client MCP',
      claudeAi: 'claude.ai',
      claudeAiHint:
        'claude.ai › Paramètres › Connecteurs › Ajouter un connecteur personnalisé : collez cette adresse, sans identifiant client. Si claude.ai en demande un, saisissez graal-ui (client public, sans secret). Claude Code garde le jeton d’agent.',
      hint: 'Créez un jeton d’agent dans Paramètres › Jetons et agents, puis exportez-le dans GRAAL_AGENT_TOKEN (ne le committez jamais).',
      copy: 'Copier la configuration',
      copied: 'Configuration copiée',
      permissions: 'Chaque action d’un agent est journalisée et visible ici en direct.',
    },
  },
  editor: {
    title: 'Éditeur low-code',
    pipeline: 'Pipeline',
    saved: 'Enregistré',
    run: 'Exécuter',
    exportCode: 'Exporter le code',
    exportPyspark: 'PySpark',
    exportPandas: 'Pandas',
    createJob: 'Créer un job',
    createJobUnsaved: 'Enregistrez le graphe : le job recopie la version enregistrée.',
    createJobNoProject: 'Aucun projet dans cette organisation : créez-en un pour y ranger le job.',
    library: 'Bibliothèque',
    properties: 'Propriétés',
    canvas: 'Canevas du pipeline',
    kinds: {
      source: 'Source',
      transform: 'Transformation',
      sink: 'Destination',
    },
    nodes: {
      readS3: 'Lire S3',
      readSql: 'Lire SQL',
      filter: 'Filtrer',
      select: 'Sélectionner',
      join: 'Joindre',
      aggregate: 'Agréger',
      writeParquet: 'Écrire Parquet',
      writeTable: 'Écrire une table SQL',
      sort: 'Trier',
      distinct: 'Dédoublonner',
      rename: 'Renommer une colonne',
      drop: 'Supprimer des colonnes',
      union: 'Unir (concaténer)',
      limit: 'Limiter',
      sample: 'Échantillonner',
      compute: 'Colonne calculée',
      cast: 'Convertir le type',
      fillNull: 'Remplacer les nulls',
      dropNull: 'Supprimer les nulls',
      pivot: 'Pivoter',
      unpivot: 'Dépivoter',
      window: 'Fenêtre (rang, cumul, décalage, glissante)',
      explode: 'Éclater une liste',
      string: 'Transformer le texte',
      datePart: 'Extraire de la date',
      deduplicate: 'Dédoublonner par clé',
      regex: 'Expression régulière',
      dateParse: 'Lire une date',
      bin: 'Découper en tranches',
      readSftp: 'Lire SFTP',
      writeSftp: 'Écrire SFTP',
      readHdfs: 'Lire HDFS',
      writeHdfs: 'Écrire HDFS',
      readHttp: 'Lire une API REST',
      readSas: 'Lire SAS (sas7bdat)',
      readJson: 'Lire JSON',
      writeJson: 'Écrire JSON',
      readIceberg: 'Lire Iceberg',
      writeIceberg: 'Écrire Iceberg',
      readCdc: 'CDC PostgreSQL',
      expect: 'Contrôles qualité',
    },
    fields: {
      path: 'Chemin',
      format: 'Format',
      condition: 'Condition',
      groupBy: 'Grouper par',
      aggregations: 'Agrégations',
      mode: 'Mode d’écriture',
      partitionBy: 'Partitionner par',
      structure: 'Structure',
      structureHelp:
        'Colonnes de la source, « colonne:type » séparées par des virgules. Types : str, int32, int64, double, bool (les dates se lisent en str). Requise en amont d’une jointure ou d’une agrégation.',
      columns: 'Colonnes',
      on: 'Clé de jointure',
      how: 'Type de jointure',
      columnsHelp: 'Colonnes séparées par des virgules ; vide = toutes les colonnes.',
      sortBy: 'Trier par',
      sortByHelp: 'Colonnes séparées par des virgules, chacune suivie de asc (défaut) ou desc.',
      oldName: 'Colonne à renommer',
      newName: 'Nouveau nom',
      distinct: 'Retirer les doublons',
      n: 'Nombre de lignes',
      fraction: 'Part des lignes',
      fractionHelp: 'Entre 0 (exclu) et 1 : 0.1 garde environ une ligne sur dix.',
      seed: 'Graine (facultative)',
      output: 'Colonne produite',
      outputHelp: 'Vide : la colonne transformée est remplacée.',
      expression: 'Expression',
      expressionHelp: 'Colonnes numériques, nombres, + - * / et parenthèses. Une division rend un décimal.',
      casts: 'Conversions',
      castsHelp: '« colonne:type » séparées par des virgules. Types : str, int32, int64, double, bool.',
      values: 'Valeurs de remplacement',
      valuesHelp: '« colonne = valeur » séparées par des virgules ; texte entre apostrophes.',
      index: 'Colonnes gardées en lignes',
      pivotColumn: 'Colonne à pivoter (texte)',
      valueColumn: 'Colonne des valeurs',
      aggregation: 'Agrégation',
      pivotValues: 'Valeurs devenues colonnes',
      pivotValuesHelp:
        'Les valeurs de la colonne pivotée, séparées par des virgules : chacune devient une colonne.',
      idColumns: 'Colonnes gardées',
      valueColumns: 'Colonnes dépivotées',
      variableColumn: 'Colonne des noms',
      function: 'Fonction',
      orderBy: 'Ordre dans la fenêtre',
      orderByHelp: 'Colonnes séparées par des virgules, chacune suivie de asc (défaut) ou desc.',
      column: 'Colonne',
      columnHelp: 'Colonne lue : cumulée, décalée ou moyennée (numérique, sauf pour un décalage).',
      separator: 'Séparateur',
      separatorHelp: 'Pris littéralement : « | » ou « . » ne sont pas des expressions régulières.',
      operation: 'Opération',
      part: 'Partie de la date',
      name: 'Nom',
      nameHelp: 'Lettres minuscules, chiffres et tirets. Sert de nom de fichier à l’export.',
      description: 'Description',
      pathHelp: 'Chemin du fichier sur le serveur, sans « .. » ni schéma.',
      dbms: 'SGBD',
      host: 'Hôte',
      port: 'Port',
      portHelp: 'Vide : le port par défaut du SGBD (5432, 3306, 1433, 1521).',
      database: 'Base',
      databaseHelp: 'Pour Oracle : le nom de service.',
      schema: 'Schéma',
      schemaHelp: 'Vide : le schéma par défaut de la session.',
      table: 'Table',
      cdcTableHelp: '« schéma.table », ou « table » (schéma public). Une seule table par tâche.',
      user: 'Utilisateur',
      userHelp: 'Facultatif : l’utilisateur HDFS (authentification simple).',
      password: 'Mot de passe',
      passwordHelp: 'Une référence ${secret:<id>} à un secret de la plateforme, jamais la valeur.',
      privateKey: 'Clé privée',
      privateKeyHelp:
        'Ou une clé privée PEM, par référence ${secret:<id>} : mot de passe OU clé, pas les deux.',
      token: 'Jeton',
      tokenHelp: 'Une référence ${secret:<id>} au jeton, jamais la valeur.',
      sslMode: 'Chiffrement TLS',
      hostKey: 'Empreinte de la clé d’hôte',
      hostKeyHelp:
        'Obligatoire : vérifiée AVANT toute authentification. Format d’OpenSSH (ssh-keygen -lf) : SHA256: suivi de 43 caractères.',
      encoding: 'Encodage',
      lines: 'Une ligne par enregistrement',
      namenode: 'Namenode',
      httpPort: 'Port WebHDFS (Pandas)',
      rpcPort: 'Port RPC (PySpark)',
      baseUrl: 'Adresse de l’API',
      baseUrlHelp: 'https://hôte ou https://hôte:port, sans chemin ni identifiants.',
      query: 'Paramètres de requête',
      queryHelp: '« nom=valeur » reliés par &, pris littéralement.',
      recordsPath: 'Chemin des enregistrements',
      recordsPathHelp: 'Chemin pointé vers la liste dans chaque réponse ; vide : la réponse entière.',
      pagination: 'Pagination',
      pageParam: 'Paramètre de page',
      pageStart: 'Première page',
      pageStartHelp: 'Vide : 1.',
      sizeParam: 'Paramètre de taille',
      pageSize: 'Taille d’une page',
      cursorPath: 'Chemin du curseur',
      cursorPathHelp: 'Chemin pointé, dans la réponse, du curseur de la page suivante.',
      maxPages: 'Pages au plus',
      auth: 'Authentification',
      header: 'En-tête',
      headerHelp: 'Nom de l’en-tête qui porte le jeton (lettres, chiffres, tirets).',
      namespace: 'Espace de noms',
      namespaceHelp: 'Vide : celui du locataire (t_<locataire>), lu au lancement du run.',
      keys: 'Clés de fusion',
      keysHelp:
        'Colonnes séparées par des virgules : une ligne de même clé est remplacée ; _op = d la supprime.',
      slot: 'Slot de réplication',
      publication: 'Publication',
      create: 'Créer slot et publication s’ils manquent',
      checks: 'Contrôles',
      offset: 'Décalage (lignes)',
      offsetHelp:
        'Lignes avant (valeur précédente) ou après (valeur suivante) la ligne courante, dans sa partition.',
      size: 'Taille de la fenêtre (lignes)',
      sizeHelp:
        'La ligne courante et les précédentes de sa partition ; en tête de partition, la fenêtre est partielle.',
      pattern: 'Motif',
      patternHelp:
        'Expression régulière cherchée dans le texte, sensible à la casse. Syntaxe commune aux moteurs : classes [A-Z], \\d, quantificateurs, groupes ( ) et (?: ). Refusés : \\w, \\s, \\b, (?=…), références arrière.',
      group: 'Groupe',
      groupHelp:
        '0 : toute la correspondance ; 1, 2… : le groupe entre parenthèses. Sans correspondance : null.',
      replacement: 'Remplacer par',
      replacementHelp: 'Texte littéral, sans « \\ » ni « $ » ; vide : les correspondances sont supprimées.',
      dateFormat: 'Format de la date',
      dateFormatHelp:
        'Jour, mois et heures sur deux chiffres. Le résultat est une date ISO (AAAA-MM-JJ) ; une date impossible ou mal formée donne un null.',
      edges: 'Bornes',
      edgesHelp:
        'Nombres croissants séparés par des virgules : un intervalle va d’une borne (incluse) à la suivante (exclue).',
      labels: 'Étiquettes',
      labelsHelp: 'Une par intervalle, séparées par des virgules ; hors des bornes ou null : null.',
      deduplicateKeys: 'Clé',
      deduplicateKeysHelp:
        'Colonnes séparées par des virgules : une seule ligne est gardée par valeur de la clé.',
      deduplicateOrder: 'Ligne gardée',
      deduplicateOrderHelp:
        'La première selon cet ordre (colonne asc ou desc, séparées par des virgules) : « date desc » garde la plus récente. Nulls en dernier.',
      checksHelp:
        "Un contrôle par ligne : not_null(col), unique(a, b), between(col, min=0, max=100), regex(col, 'motif'), row_count(min=1), freshness(col, secondes), in_set(col, 'a', 'b'). Ajouter severity=warning pour ne pas faire échouer le run.",
    },
    choices: {
      distinct: { false: 'Non (UNION ALL)', true: 'Oui (UNION)' },
      how: { any: 'Si une colonne est nulle', all: 'Si toutes sont nulles' },
      aggregation: { sum: 'Somme', mean: 'Moyenne', min: 'Minimum', max: 'Maximum', count: 'Nombre' },
      function: {
        row_number: 'Numéro de ligne',
        rank: 'Rang (avec ex aequo)',
        dense_rank: 'Rang dense',
        cumulative_sum: 'Somme cumulée',
        lag: 'Valeur précédente',
        lead: 'Valeur suivante',
        rolling_sum: 'Somme glissante',
        rolling_mean: 'Moyenne glissante',
      },
      operation: {
        upper: 'Majuscules',
        lower: 'Minuscules',
        trim: 'Retirer les espaces',
        extract: 'Extraire',
        replace: 'Remplacer',
      },
      part: { year: 'Année', quarter: 'Trimestre', month: 'Mois', day: 'Jour' },
      dbms: {
        postgresql: 'PostgreSQL',
        mysql: 'MySQL',
        mariadb: 'MariaDB',
        sqlserver: 'SQL Server',
        oracle: 'Oracle',
      },
      sslMode: {
        default: 'Défaut du pilote',
        disable: 'Désactivé',
        require: 'Exigé (certificat non vérifié)',
        verify: 'Exigé et vérifié',
      },
      mode: {
        overwrite: 'Remplacer',
        append: 'Ajouter',
        merge: 'Fusionner (sur les clés)',
      },
      format: { csv: 'CSV', parquet: 'Parquet', json: 'JSON', sas7bdat: 'SAS (sas7bdat)' },
      encoding: { infer: 'Détecter', 'utf-8': 'UTF-8', 'latin-1': 'Latin-1', cp1252: 'Windows-1252' },
      lines: { false: 'Non (tableau JSON)', true: 'Oui (JSON Lines)' },
      pagination: {
        none: 'Aucune',
        page: 'Par numéro de page',
        offset: 'Par décalage',
        cursor: 'Par curseur',
        link: 'En-tête Link (rel="next")',
      },
      auth: {
        none: 'Aucune',
        bearer: 'Jeton Bearer',
        basic: 'Basique (utilisateur, mot de passe)',
        header: 'Jeton dans un en-tête',
      },
      create: { false: 'Non', true: 'Oui' },
    },
    rows_one: '{{count, number}} ligne',
    rows_other: '{{count, number}} lignes',
    valid: 'Pipeline valide',
    zoomIn: 'Zoom avant',
    zoomOut: 'Zoom arrière',
    fitView: 'Ajuster à la vue',
    minimap: 'Mini-carte',
    subtitle: 'Dessinez un pipeline, exportez son code : il s’exécute hors de graal.',
    new: 'Nouveau graphe',
    newTitle: 'Nouveau graphe',
    newSubtitle:
      'Un graphe décrit un pipeline ; l’éditeur le transforme en code PySpark, Pandas, Polars ou SQL DuckDB.',
    created: 'Graphe « {{name}} » créé.',
    searchPlaceholder: 'Rechercher un graphe…',
    save: 'Enregistrer',
    saveDone: 'Graphe enregistré.',
    saveFailed: 'L’enregistrement a échoué.',
    delete: 'Supprimer le graphe',
    deleted: 'Graphe « {{name}} » supprimé.',
    deleteFailed: 'La suppression a échoué.',
    exported: 'Code {{language}} exporté.',
    exportFailed: 'L’export du code a échoué.',
    exportUnsupported: 'Export impossible : nœud « {{node}} » — {{reason}}.',
    dagProblems: {
      unsupportedNode: 'ce type de nœud n’a pas d’équivalent dans le générateur de code',
      badCondition: 'condition illisible (attendu : colonne opérateur valeur, reliées par « and »)',
      badJoin: 'clés de jointure absentes (attendu : colonne, ou gauche = droite)',
      badAggregation: 'agrégations illisibles (attendu : fonction(colonne), ex. mean(consommation))',
      badPath: 'chemin non reconnu (attendu : s3://bucket/chemin ou /chemin/local)',
      badStructure:
        'structure illisible près de « {{detail}} » (attendu : colonne:type, types str, int32, int64, double, bool)',
      badColumn: 'nom de colonne invalide « {{detail}} » (lettres, chiffres et _ seulement)',
      badLiteral:
        'valeur « {{detail}} » refusée : le générateur de code n’échappe pas encore les apostrophes, barres obliques inverses et retours à la ligne',
      missingStructure: 'une lecture en amont n’a pas de structure : renseignez ses colonnes et leurs types',
      unknownColumn: 'colonne « {{detail}} » absente des données qui arrivent à ce nœud',
      missingInput: 'il manque une entrée à ce nœud',
      tooManyInputs: 'ce nœud a trop d’entrées',
      badNumber: 'nombre refusé « {{detail}} »',
      badOrder: 'ordre illisible « {{detail}} » (attendu : colonne asc ou colonne desc)',
      badExpression: 'expression illisible « {{detail}} » (colonnes, nombres, + - * / et parenthèses)',
      badValue:
        'valeur de remplacement refusée « {{detail}} » (attendu : colonne = valeur, du type de la colonne)',
      missingParam: 'paramètre « {{detail}} » à renseigner',
      badType: 'type incompatible pour « {{detail}} »',
      badUnion: 'les entrées d’une union doivent avoir les mêmes colonnes',
      badHost: 'hôte refusé « {{detail}} » (nom d’hôte ou adresse IPv4)',
      badPort: 'port refusé « {{detail}} » (entier de 1 à 65535)',
      badSecret:
        'champ « {{detail}} » : une référence ${secret:<id>} est attendue, jamais une valeur (et un seul de mot de passe ou clé privée)',
      badHostKey:
        'empreinte de clé d’hôte refusée (attendu : SHA256: suivi de 43 caractères, comme ssh-keygen -lf)',
      badIdentifier: 'identifiant refusé « {{detail}} » (lettres, chiffres et _, 63 caractères au plus)',
      badUrl: 'adresse refusée « {{detail}} » (attendu : https://hôte ou https://hôte:port, sans chemin)',
      badQuery: 'paramètre de requête refusé « {{detail}} » (attendu : nom=valeur reliés par &)',
      badCheck: 'contrôle illisible « {{detail}} »',
      badSetting: 'valeur refusée pour « {{detail}} »',
      badPattern:
        'motif refusé « {{detail}} » : hors de la syntaxe commune aux moteurs (\\w, \\s, \\b, (?=…), références arrière refusés)',
      badBins: 'tranches incohérentes : des bornes croissantes, et une étiquette par intervalle',
    },
    connection: {
      label: 'Connexion',
      help: 'Pré-remplit les champs depuis une connexion du projet « {{project}} ». Le DAG recopie ces valeurs, il ne référence pas la connexion.',
      placeholder: 'Choisir une connexion',
      loading: 'Chargement des connexions du projet…',
      none: 'Aucune connexion de ce type dans le projet « {{project}} ».',
      unavailable: 'Connexions du projet « {{project}} » indisponibles.',
      applied: 'Champs pré-remplis depuis « {{name}} ».',
      types: {
        postgresql: 'PostgreSQL',
        mysql: 'MySQL',
        mariadb: 'MariaDB',
        sqlserver: 'SQL Server',
        oracle: 'Oracle',
        sftp: 'SFTP',
        hdfs: 'HDFS',
        http: 'API REST',
      },
    },
    selectNode: 'Sélectionnez un nœud pour voir ses paramètres.',
    noParams: 'Aucun paramètre renseigné',
    columns: {
      name: 'Nom',
      owner: 'Propriétaire',
      type: 'Type',
    },
    empty: {
      title: 'Aucun graphe pour l’instant',
      description: 'Un graphe est un pipeline dessiné : sources, transformations, destinations.',
    },
    languages: {
      pyspark: 'PySpark',
      pandas: 'Pandas',
      polars: 'Polars',
      duckdb: 'DuckDB (SQL)',
    },
    problems: {
      empty: 'Canevas vide',
      noSource: 'Aucune source',
      noSink: 'Aucune destination',
      disconnected: 'Un nœud n’est relié à rien',
    },
  },
  login: {
    tagline: 'Vos données. Votre infrastructure. Vos agents.',
    title: 'Connexion',
    subtitle: 'Connectez-vous à l’espace de votre organisation.',
    tenantLabel: 'Organisation',
    tenantPlaceholder: 'ex. energie-demo',
    tenantHint: 'Identifiant fourni par votre administrateur.',
    continue: 'Continuer',
    redirecting: 'Redirection vers le fournisseur d’identité…',
    platformAdmin: 'Administrateur de la plateforme : se connecter avec mon compte graal',
    sso: 'Authentification unique (SSO) auprès du fournisseur d’identité de votre organisation.',
    required: 'Saisissez l’identifiant de votre organisation.',
    invalid: 'Identifiant invalide : lettres minuscules, chiffres et tirets.',
    unknownTenant: 'Organisation inconnue : vérifiez l’identifiant auprès de votre administrateur.',
    unreachable: 'Service d’authentification injoignable. Réessayez dans un instant.',
    hostedOn: 'Hébergé sur : {{infra}} · {{country}}',
    documentation: 'Documentation',
    privacy: 'Confidentialité',
    security: 'Sécurité',
    version: 'Version {{version}}',
  },
  callback: {
    pending: 'Connexion en cours…',
    failedTitle: 'Connexion interrompue',
    failed:
      'Le fournisseur d’identité n’a pas confirmé la connexion. Recommencez depuis la page de connexion.',
    retry: 'Revenir à la connexion',
  },
  overview: {
    title: 'Vue d’ensemble',
    subtitle: 'Activité du projet {{project}}.',
    runsTotal: 'Runs',
    runsRunning: 'En cours',
    runsSucceeded: 'Réussis',
    runsFailed: 'En échec',
    recent: 'Activité récente',
    empty: 'Aucun run pour l’instant.',
    emptyHelp: 'Lancez un job ou un workflow : ses exécutions apparaîtront ici.',
    searchPlaceholder: 'Rechercher un job…',
    viewJobs: 'Voir les jobs',
    columns: {
      job: 'Job',
      run: 'Run',
      status: 'Statut',
      triggeredBy: 'Déclenché par',
      started: 'Démarré',
      duration: 'Durée',
    },
  },
  designLab: {
    title: 'Design lab',
    theme: 'Thème',
    mode: 'Mode',
    view: 'Vue',
    language: 'Langue',
    recommended: 'recommandé',
    views: {
      jobs: 'Jobs',
      run: 'Run + logs',
      palette: 'Palette ⌘K',
      agents: 'Agents ⌘J',
      editor: 'Éditeur',
      login: 'Login',
    },
  },
  mfa: {
    title: 'Double facteur',
    subtitle:
      'Le second facteur imposé à la connexion dans l’organisation {{tenant}} : aux administrateurs, ou à tous ses membres.',
    current: {
      OFF: 'Aucun second facteur n’est imposé.',
      ADMINS: 'Second facteur imposé aux administrateurs ({{method}}).',
      ALL: 'Second facteur imposé à tous les membres ({{method}}).',
    },
    administrators_one: '{{count}} compte administrateur y est soumis.',
    administrators_other: '{{count}} comptes administrateurs y sont soumis.',
    enrolment:
      'Un compte sans second facteur configuré est invité à le configurer à sa prochaine connexion. Un facteur déjà configuré n’est jamais supprimé.',
    fields: {
      policy: 'Politique',
      policyHelp:
        'À qui le second facteur est imposé. Les comptes de service (jetons d’agent) ne sont pas concernés.',
      method: 'Méthode',
      methodHelp: 'Code à usage unique d’une application (TOTP) ou clé de sécurité / passkey (WebAuthn).',
    },
    policies: {
      OFF: 'Non imposé',
      ADMINS: 'Obligatoire pour les administrateurs',
      ALL: 'Obligatoire pour tous',
    },
    methods: {
      OTP: 'Application d’authentification (TOTP)',
      WEBAUTHN: 'Clé de sécurité ou passkey (WebAuthn)',
    },
    save: 'Appliquer',
    saved: 'Politique de double facteur appliquée au realm.',
  },
  branding: {
    title: 'Marque blanche',
    subtitle:
      'Le nom, le logo, la couleur principale et le thème de connexion de l’organisation {{tenant}}, vus par tous ses membres.',
    preview: 'Aperçu',
    sampleButton: 'Bouton principal',
    logoAlt: 'Logo de l’organisation',
    logoFailed: 'Le logo ne se charge pas : la console garde celui de graal.',
    lowContrast:
      'Contraste de {{ratio}}:1 avec le texte blanc des boutons, sous le minimum de {{min}}:1 (WCAG AA) : choisissez une couleur plus sombre.',
    saved: 'Marque blanche enregistrée.',
    loginThemes: { graal: 'graal', keycloak: 'Keycloak (par défaut)' },
    fields: {
      brandName: 'Nom de la marque',
      brandNameHelp: 'Remplace « graal » dans la barre latérale (64 caractères au plus). Vide : graal.',
      brandNamePlaceholder: 'Énergie Démo',
      logoUrl: 'URL du logo',
      logoUrlHelp: 'Une image en https, sans identifiants. Si elle ne se charge pas, le logo graal reste.',
      primaryColor: 'Couleur principale',
      primaryColorHelp: 'Au format #RRGGBB : l’aplat du bouton principal et du sigle. Vide : celle de graal.',
      loginTheme: 'Thème de connexion',
      loginThemeHelp: 'La page de connexion du realm Keycloak de l’organisation.',
      customDomain: 'Domaine personnalisé',
      customDomainHelp:
        'Le nom d’hôte sous lequel l’organisation sert la console (sans https://). Il faut un enregistrement DNS vers graal et un certificat : voir INSTALL.md §12.',
    },
  },
  security: {
    roles: {
      title: 'Rôles',
      subtitle: 'Les rôles RBAC de l’organisation {{tenant}} : ce que chacun peut faire, et sur quoi.',
      searchPlaceholder: 'Rechercher un rôle…',
      new: 'Nouveau rôle',
      newTitle: 'Nouveau rôle',
      newSubtitle:
        'Un rôle regroupe des permissions. On l’affecte ensuite à un utilisateur, un groupe ou un agent, sur une ressource.',
      columns: {
        name: 'Nom',
        type: 'Type',
        description: 'Description',
        holders: 'Titulaires',
        updated: 'Modifié',
      },
      types: { internal: 'Système', custom: 'Personnalisé' },
      fields: {
        name: 'Nom',
        nameHelp: 'Un libellé lisible, unique dans l’organisation.',
        description: 'Description',
      },
      empty: { title: 'Aucun rôle', description: 'Créez un premier rôle pour organiser les droits.' },
      created: 'Rôle « {{name}} » créé.',
      deleted: 'Rôle « {{name}} » supprimé.',
      deleteFailed: 'La suppression du rôle a échoué : {{detail}}',
      delete: 'Supprimer le rôle',
      confirmDelete: 'Supprimer le rôle « {{name}} » ? Ses affectations seront révoquées.',
      detail: {
        subtitle_one: 'Rôle {{type}} · {{count}} titulaire',
        subtitle_other: 'Rôle {{type}} · {{count}} titulaires',
        tabs: { summary: 'Résumé', holders: 'Titulaires' },
        permissions: 'Permissions',
        permissionsHint:
          'Motifs portés par ce rôle. « /* » accorde tout ; « /job/* » accorde toutes les actions sur les jobs.',
        noPermissions: 'Ce rôle ne porte aucune permission.',
        holdersHint: 'Qui porte ce rôle, et sur quelle ressource. Révoquer retire immédiatement les droits.',
        identifier: 'Identifiant',
        created: 'Créé',
        updated: 'Modifié',
      },
    },
    assignments: {
      title: 'Affectations de rôles',
      subtitle: 'Qui porte quel rôle, sur quelle ressource — utilisateurs, groupes et agents.',
      searchPlaceholder: 'Rechercher un principal, un rôle, une ressource…',
      columns: { principal: 'Principal', role: 'Rôle', resource: 'Ressource', since: 'Depuis' },
      principalTypes: { user: 'Utilisateur', group: 'Groupe', identity: 'Agent', client: 'Jeton' },
      resourceTypes: {
        tenant: 'Organisation',
        project: 'Projet',
        job: 'Job',
        workflow: 'Workflow',
        secret: 'Secret',
      },
      filters: { principalType: 'Principal', resourceType: 'Ressource' },
      revoke: 'Révoquer',
      confirmRevoke:
        'Révoquer le rôle « {{role}} » de {{principal}} ? Les droits sont retirés immédiatement.',
      revoked: 'Rôle « {{role}} » révoqué pour {{principal}}.',
      revokeFailed: 'La révocation a échoué : {{detail}}',
      permissionsOf: 'Permissions du rôle',
      showPermissions: 'Voir les permissions',
      empty: {
        title: 'Aucune affectation',
        description: 'Aucun rôle n’est affecté dans cette organisation.',
      },
      unknownPrincipal: 'Principal inconnu',
      unknownRole: 'Rôle inconnu',
    },
    audit: {
      title: 'Connexions',
      subtitle: 'Historique des connexions à l’organisation {{tenant}} — utilisateurs et agents.',
      searchPlaceholder: 'Rechercher un utilisateur, une adresse…',
      columns: { when: 'Quand', type: 'Événement', user: 'Qui', ip: 'Adresse IP', location: 'Lieu' },
      types: { login: 'Connexion', logout: 'Déconnexion', login_error: 'Connexion refusée' },
      filters: { type: 'Événement', kind: 'Principal' },
      empty: {
        title: 'Aucun événement',
        description: 'Aucune connexion n’a encore été enregistrée pour cette organisation.',
      },
      scopeNote:
        'Cet écran couvre les connexions. Les actions des humains et des agents sont dans le journal d’audit.',
    },
    auditActions: {
      title: 'Journal d’audit',
      subtitle: 'Les actions menées dans l’organisation {{tenant}} — par des humains et par des agents.',
      scopeNote:
        'Une ligne par lecture et par écriture de l’API, et par refus de droit. Jamais de corps de requête, de secret ni de jeton.',
      logins: 'Connexions',
      loadMore: 'Charger plus',
      project: 'Projet {{id}}',
      columns: {
        when: 'Quand',
        who: 'Qui',
        action: 'Action',
        resource: 'Ressource',
        actor: 'Acteur',
        kind: 'Accès',
        result: 'Résultat',
        origin: 'Origine',
      },
      actors: { HUMAN: 'Humain', AGENT: 'Agent', DEVICE: 'Appareil' },
      accessKinds: { READ: 'Lecture', WRITE: 'Écriture' },
      export: {
        label: 'Exporter',
        csv: 'Exporter en CSV',
        json: 'Exporter en JSON',
        done: 'Export enregistré : {{filename}}',
        failed: 'L’export a échoué',
      },
      results: { ALLOWED: 'Autorisé', DENIED: 'Refusé' },
      periods: {
        '24h': 'Dernières 24 h',
        '7d': '7 derniers jours',
        '30d': '30 derniers jours',
        all: 'Toute la période',
      },
      filters: {
        period: 'Période',
        actor: 'Humain, agent ou appareil',
        allActors: 'Tous les acteurs',
        kind: 'Lecture ou écriture',
        allKinds: 'Lectures et écritures',
        result: 'Résultat',
        allResults: 'Tous les résultats',
        action: 'Action',
        actionPlaceholder: 'Action, ex. /job/create',
        project: 'Projet',
        projectPlaceholder: 'Identifiant du projet',
        principal: 'Principal',
        principalPlaceholder: 'Identifiant du principal',
      },
      empty: {
        title: 'Aucune action',
        description: 'Aucune action ne correspond à ces filtres.',
      },
    },
    common: {
      unknownDetail: 'erreur inconnue',
    },
  },
  access: {
    common: {
      identifier: 'Identifiant',
      description: 'Description',
      created: 'Créé',
      saved: 'Modifications enregistrées.',
      deleteFailed: 'La suppression a échoué : {{detail}}',
      deleteForbidden:
        'Suppression refusée : vos droits ne la couvrent pas. Demandez-la à un administrateur de l’organisation.',
      neverShown: 'Jamais réaffiché',
      copied: 'Copié dans le presse-papiers.',
      copyFailed: 'Copie impossible : sélectionnez le texte à la main.',
      copyLabel: 'Copier : {{what}}',
    },
    rights: {
      title: 'Droits',
      hint: 'Les rôles portés par ce principal, et sur quelle ressource. Révoquer retire immédiatement le droit.',
      empty: {
        title: 'Aucun rôle',
        description: 'Aucun rôle n’a été accordé à ce principal.',
      },
    },
    grant: {
      title: 'Accorder un rôle',
      principalType: 'Accorder à',
      types: { user: 'Un utilisateur', group: 'Un groupe', client: 'Un jeton (agent ou API)' },
      principals: { user: 'Utilisateur', group: 'Groupe', client: 'Jeton' },
      clientOption: '{{name}} — {{kind}}',
      role: 'Rôle',
      roleHelp: 'Les permissions du rôle s’appliquent sur cette ressource seulement.',
      submit: 'Accorder',
      granted: 'Rôle accordé.',
    },
    tokens: {
      title: 'Jetons et agents',
      subtitle:
        'Les identités machine de l’organisation {{tenant}} : jetons d’agent (MCP) et jetons API personnels.',
      new: 'Nouveau jeton',
      searchPlaceholder: 'Rechercher un jeton, un identifiant client…',
      columns: {
        name: 'Nom',
        kind: 'Sorte',
        clientId: 'Identifiant client',
        rights: 'Rôles',
        created: 'Créé',
      },
      kinds: { agent: 'Agent', api: 'API personnel' },
      kindHelp: {
        agent:
          'Jeton d’agent : il n’a aucun droit tant qu’un rôle ne lui est pas accordé. C’est l’identité à donner à un agent IA via MCP.',
        api: 'Jeton API personnel : il agit avec vos propres droits, sans affectation propre.',
      },
      inheritsShort: 'vos droits',
      empty: {
        title: 'Aucun jeton',
        description: 'Créez un jeton d’agent pour brancher un agent IA (MCP) sur cette organisation.',
      },
      fields: {
        kind: 'Sorte de jeton',
        name: 'Nom',
        nameHelp: 'Un nom lisible : il apparaît dans l’audit et dans les affectations.',
        description: 'Description',
        project: 'Projet',
        projectHelp: 'Le seul projet sur lequel l’agent pourra agir.',
        role: 'Rôle',
        roleHelp: 'Ce que l’agent pourra faire sur ce projet.',
      },
      wizard: {
        title: 'Nouveau jeton',
        subtitle: 'Une identité machine pour un agent IA ou un script. Son secret ne s’affiche qu’une fois.',
        steps: { kind: 'Sorte', identity: 'Identité', rights: 'Droits', review: 'Récapitulatif' },
        rightsHelp:
          'Le rôle est accordé sur ce projet juste après la création. Sans lui, l’agent ne pourrait rien faire.',
        agentReserved:
          'Un jeton d’agent crée une identité de l’organisation : il demande le droit de créer des identités, que vous n’avez pas. Un jeton API personnel, lui, est ouvert à tous.',
        submit: 'Créer le jeton',
      },
      reveal: {
        heading: 'Jeton créé',
        subtitle: 'Copiez son secret maintenant, puis branchez votre agent.',
        title: 'Le jeton « {{name}} » est créé.',
        clientId: 'Identifiant client (client_id)',
        secret: 'Secret client',
        copySecret: 'Copier le secret',
        once: 'Ce secret ne sera plus jamais affiché. Perdu, il faudra supprimer le jeton et en créer un autre.',
        noSecret: 'Le serveur n’a rendu aucun secret pour ce jeton : supprimez-le et créez-en un autre.',
        granted: 'Rôle « {{role}} » accordé sur le projet {{project}}.',
        grantFailed:
          'Le jeton est créé, mais le rôle n’a pas pu lui être accordé : {{detail}}. Accordez-le depuis l’onglet Droits du jeton.',
        done: 'J’ai copié le secret',
        doneHint: 'Le secret disparaît de l’écran.',
      },
      connect: {
        title: 'Brancher un agent',
        step1:
          '1. Obtenir un jeton d’accès (collez le secret quand la commande l’attend : rien ne s’affiche)',
        step1Hint:
          'Le jeton d’accès expire à la durée fixée par l’organisation : pour le renouveler, relancez cette commande, puis redémarrez Claude Code depuis ce même terminal.',
        step2: '2. Déclarer le serveur MCP dans Claude Code (une seule fois)',
        step2Hint:
          'Les guillemets simples comptent : la configuration garde la variable, pas le jeton, et Claude Code la relit à chaque démarrage. Le serveur relaie le jeton présenté : l’agent agit avec les droits de ce jeton, et seulement eux.',
        noMcp: 'Aucune adresse de serveur MCP n’est configurée sur cette installation (GRAAL_MCP_URL).',
      },
      detail: {
        subtitle: 'Jeton {{kind}} · créé {{when}}',
        tabs: { summary: 'Résumé', rights: 'Droits', definition: 'Définition' },
        owner: 'Créé par',
        secretNote: 'Son secret n’est jamais réaffiché : perdu, supprimez le jeton et créez-en un autre.',
        rightsHint:
          'Les rôles de cet agent. Révoquer retire le droit à la requête suivante de l’agent — c’est ce que montre l’acte 7b.',
        grant: 'Accorder un rôle',
        grantTitle: 'Accorder un rôle sur un projet',
        delete: 'Supprimer le jeton',
        confirmDelete:
          'Supprimer le jeton « {{name}} » ? Tout agent qui l’utilise perd l’accès immédiatement.',
        deleted: 'Jeton « {{name}} » supprimé.',
      },
    },
    secrets: {
      title: 'Secrets',
      subtitle:
        'Valeurs sensibles de l’organisation {{tenant}}, chiffrées en base. Un secret se saisit ou se remplace ; il ne se relit jamais.',
      new: 'Nouveau secret',
      searchPlaceholder: 'Rechercher un secret…',
      columns: { name: 'Nom', description: 'Description', labels: 'Étiquettes', updated: 'Modifié' },
      empty: {
        title: 'Aucun secret',
        description: 'Déclarez un mot de passe ou une clé ici plutôt que dans les options d’un job.',
      },
      fields: {
        name: 'Nom',
        nameHelp:
          'La clé que lit un job (fichier unseal.properties) : lettres, chiffres, « _ », « . » et « - », sans espace.',
        description: 'Description',
        value: 'Valeur',
        newValue: 'Nouvelle valeur',
        valueHelp: 'Saisie masquée, sur une ligne. Elle ne sera plus jamais affichée.',
      },
      newTitle: 'Nouveau secret',
      newSubtitle: 'La valeur est chiffrée avant d’être écrite, et ne sera plus jamais affichée.',
      created: 'Secret « {{name}} » créé.',
      detail: {
        subtitle: 'Secret · modifié {{when}}',
        tabs: { summary: 'Résumé', definition: 'Définition', value: 'Valeur', access: 'Accès' },
        value: 'Valeur',
        sealed: 'Chiffrée — jamais réaffichée',
        usage:
          'Un job lit ce secret dans /workspace/secrets/unseal.properties, sous son nom. Sa valeur ne passe ni par la console, ni par les options du job.',
        renameHint: 'Renommer un secret change la clé que lisent les jobs : mettez-les à jour.',
        rotateHint:
          'Remplacez la valeur sans jamais lire l’ancienne. Les prochains runs liront la nouvelle valeur.',
        rotate: 'Remplacer la valeur',
        managedKey:
          'Clé du bucket géré « {{bucket}} » : la plateforme la pose et la garde alignée sur son utilisateur S3. Sa valeur ne se remplace pas ici.',
        rotated: 'Valeur du secret « {{name}} » remplacée.',
        accessTitle: 'Accès',
        accessHint:
          'Qui peut lire ou modifier ce secret. Un secret appartient à l’organisation : son accès se donne par rôle, secret par secret.',
        accessEmpty: {
          title: 'Aucun accès accordé',
          description: 'Seuls les administrateurs de l’organisation peuvent lire ce secret.',
        },
        delete: 'Supprimer le secret',
        confirmDelete: 'Supprimer le secret « {{name}} » ? Les jobs qui le lisent ne le trouveront plus.',
        deleted: 'Secret « {{name}} » supprimé.',
      },
    },
    libraries: {
      title: 'Librairies',
      subtitle: 'Code déposé pour les jobs de l’organisation {{tenant}} : wheels, requirements, JAR.',
      scopeNote: 'Visibles par toute l’organisation : aucun droit par librairie n’est encore appliqué.',
      searchPlaceholder: 'Rechercher un fichier…',
      upload: 'Déposer',
      uploadInput: 'Fichiers à déposer',
      uploaded_one: '{{count}} fichier déposé.',
      uploaded_other: '{{count}} fichiers déposés.',
      uploadFailed: 'Le dépôt a échoué : {{detail}}',
      columns: {
        filename: 'Fichier',
        size: 'Taille',
        type: 'Type',
        key: 'Identifiant',
        modified: 'Déposé',
      },
      download: 'Télécharger {{name}}',
      downloadFailed: 'Le téléchargement a échoué : {{detail}}',
      delete: 'Supprimer la librairie',
      deleteLabel: 'Supprimer {{name}}',
      confirmDelete: 'Supprimer « {{name}} » ? Les jobs qui l’installent échoueront au prochain run.',
      deleted: 'Librairie « {{name}} » supprimée.',
      empty: {
        title: 'Aucune librairie',
        description: 'Déposez un paquet Python (.whl), un requirements.txt ou un JAR.',
      },
    },
    members: {
      title: 'Membres',
      subtitle: 'Les utilisateurs de l’organisation {{tenant}}.',
      new: 'Nouvel utilisateur',
      searchPlaceholder: 'Rechercher un nom, un identifiant, une adresse…',
      columns: {
        name: 'Nom',
        username: 'Identifiant',
        email: 'Adresse e-mail',
        origin: 'Origine',
        created: 'Arrivé',
      },
      locked: 'Verrouillé',
      empty: { title: 'Aucun utilisateur', description: 'Créez un premier utilisateur de l’organisation.' },
      fields: {
        username: 'Identifiant',
        usernameHelp: 'Minuscules, chiffres, « . », « _ » et « - » : c’est l’identifiant de connexion.',
        firstname: 'Prénom',
        lastname: 'Nom',
        email: 'Adresse e-mail',
        description: 'Description',
        password: 'Mot de passe initial',
        passwordHelp: 'Au moins {{min}} caractères. Il n’est jamais réaffiché.',
        confirm: 'Confirmer le mot de passe',
      },
      errors: {
        username: 'Minuscules, chiffres, « . », « _ » et « - » ; 2 à 63 caractères.',
        email: 'Adresse e-mail invalide.',
        passwordLength: 'Au moins {{min}} caractères.',
        mismatch: 'Les deux mots de passe diffèrent.',
      },
      newTitle: 'Nouvel utilisateur',
      newSubtitle: 'L’utilisateur est créé dans l’annuaire de l’organisation, avec son mot de passe initial.',
      created: 'Utilisateur « {{name}} » créé.',
      detail: {
        tabs: { summary: 'Résumé', definition: 'Définition', rights: 'Droits', activity: 'Activité' },
        activityTitle: 'Connexions',
        delete: 'Supprimer l’utilisateur',
        confirmDelete: 'Supprimer l’utilisateur « {{name}} » ? Il ne pourra plus se connecter.',
        deleted: 'Utilisateur « {{name}} » supprimé.',
      },
    },
    groups: {
      title: 'Groupes',
      subtitle: 'Les groupes de l’organisation {{tenant}}.',
      new: 'Nouveau groupe',
      searchPlaceholder: 'Rechercher un groupe…',
      columns: { name: 'Nom', description: 'Description', roles: 'Rôles', created: 'Créé' },
      empty: { title: 'Aucun groupe', description: 'Créez un groupe pour réunir des utilisateurs.' },
      fields: {
        name: 'Nom',
        nameHelp: 'Un nom court, unique dans l’organisation.',
        description: 'Description',
      },
      newTitle: 'Nouveau groupe',
      newSubtitle: 'Un groupe reçoit des rôles, comme un utilisateur.',
      created: 'Groupe « {{name}} » créé.',
      detail: {
        tabs: { summary: 'Résumé', rights: 'Droits' },
        membersNote:
          'Les membres d’un groupe ne se gèrent pas encore depuis la console : aucune opération d’appartenance n’est exposée, et les rôles d’un groupe ne s’appliquent pas encore à ses membres.',
        delete: 'Supprimer le groupe',
        confirmDelete: 'Supprimer le groupe « {{name}} » ?',
        deleted: 'Groupe « {{name}} » supprimé.',
      },
    },
    identityProviders: {
      title: 'Fournisseurs d’identité',
      subtitle:
        'Connexion des utilisateurs de l’organisation {{tenant}} par un annuaire externe, fédéré dans son realm Keycloak.',
      new: 'Nouveau fournisseur',
      searchPlaceholder: 'Rechercher un fournisseur…',
      columns: { name: 'Nom', type: 'Fournisseur', clientId: 'Identifiant client', created: 'Créé' },
      types: {
        microsoft: 'Microsoft Entra ID',
        google: 'Google',
        github: 'GitHub',
        gitlab: 'GitLab',
        'linkedin-openid-connect': 'LinkedIn',
        oidc: 'OpenID Connect',
        'keycloak-oidc': 'Keycloak (OpenID Connect)',
        saml: 'SAML 2.0',
        ldap: 'Annuaire LDAP',
        'active-directory': 'Active Directory',
      },
      nameIdFormats: {
        persistent: 'Persistant',
        transient: 'Transitoire',
        emailAddress: 'Adresse e-mail',
        unspecified: 'Non spécifié',
      },
      principalTypes: {
        SUBJECT: 'Sujet (NameID)',
        ATTRIBUTE: 'Attribut',
        FRIENDLY_ATTRIBUTE: 'Attribut (nom usuel)',
      },
      searchScopes: { subtree: 'Toute la sous-arborescence', one: 'Un seul niveau' },
      empty: {
        title: 'Aucun fournisseur',
        description:
          'Fédérez l’annuaire de l’organisation (Entra ID, OpenID Connect, SAML, LDAP, Active Directory…) pour la connexion.',
      },
      fields: {
        name: 'Nom',
        nameHelp: 'Le libellé du bouton de connexion.',
        description: 'Description',
        type: 'Fournisseur',
        clientId: 'Identifiant client',
        clientIdHelp: 'L’identifiant de l’application déclarée chez le fournisseur.',
        clientSecret: 'Secret client',
        clientSecretHelp: 'Saisie masquée. Il n’est jamais réaffiché.',
        typeHelp:
          'Fournisseur social, OpenID Connect ou SAML (courtage), ou annuaire LDAP / Active Directory (fédération en lecture seule).',
        discoveryUrl: 'URL de découverte',
        discoveryUrlHelp:
          'Le document .well-known/openid-configuration du fournisseur. À défaut, les URL d’autorisation et de jeton.',
        authorizationUrl: 'URL d’autorisation',
        tokenUrl: 'URL de jeton',
        metadataUrl: 'URL des métadonnées',
        metadataUrlHelp: 'Le descripteur SAML de l’IdP. À défaut, l’URL SSO et l’identifiant d’entité.',
        ssoUrl: 'URL SSO',
        entityId: 'Identifiant d’entité de l’IdP',
        signingCertificate: 'Certificat de signature',
        signingCertificateHelp: 'Le certificat X.509 (PEM ou base64) qui vérifie les signatures de l’IdP.',
        nameIdFormat: 'Format du NameID',
        principalType: 'Identifiant de l’utilisateur',
        principalTypeHelp: 'Ce qui identifie l’utilisateur dans l’assertion SAML.',
        principalAttribute: 'Attribut de l’utilisateur',
        connectionUrl: 'URL de connexion',
        connectionUrlHelp: 'ldap:// ou ldaps://. La connexion est testée avant la création.',
        usersDn: 'DN des utilisateurs',
        bindDn: 'DN de liaison',
        bindDnHelp: 'Le compte de service qui lit l’annuaire. Sans lui ni mot de passe : liaison anonyme.',
        bindCredential: 'Mot de passe de liaison',
        usernameAttribute: 'Attribut du nom d’utilisateur',
        usernameAttributeHelp: 'uid pour LDAP, sAMAccountName pour Active Directory, s’il est laissé vide.',
        searchScope: 'Portée de recherche',
        secretKeepHelp: 'Saisie masquée. Laissez vide pour garder la valeur en place, jamais réaffichée.',
      },
      newTitle: 'Nouveau fournisseur d’identité',
      newSubtitle:
        'Déclarez d’abord une application chez le fournisseur ; son URI de redirection s’affiche ensuite ici.',
      created: 'Fournisseur « {{name}} » créé.',
      detail: {
        tabs: { summary: 'Résumé', definition: 'Définition' },
        redirectUri: 'URI de redirection',
        redirectUriHelp: 'À déclarer dans l’application du fournisseur, telle quelle.',
        configuration: 'Configuration',
        configurationEmpty: 'Aucune configuration propre : celle du fournisseur social de Keycloak.',
        federationNote:
          'Fédération d’utilisateurs en lecture seule : les comptes de l’annuaire se connectent avec leur mot de passe habituel, sans redirection.',
        credentialsNote:
          'L’identifiant, le secret et la configuration se modifient dans l’onglet Définition et sont reportés à Keycloak ; le type, lui, ne change pas : supprimez le fournisseur et déclarez-le de nouveau.',
        delete: 'Supprimer le fournisseur',
        confirmDelete:
          'Supprimer le fournisseur « {{name}} » ? Ses utilisateurs ne pourront plus se connecter par lui.',
        deleted: 'Fournisseur « {{name}} » supprimé.',
      },
    },
    projectMembers: {
      title: 'Membres du projet',
      short: 'Membres',
      subtitle: 'Qui a accès au projet {{project}}, et avec quel rôle — utilisateurs, groupes et agents.',
      searchPlaceholder: 'Rechercher un membre, un rôle…',
      add: 'Ajouter un membre',
      addTitle: 'Ajouter un membre',
      addSubtitle: 'Accorder un rôle sur le projet {{project}}.',
      tenantMembers: 'Membres de l’organisation',
      empty: {
        title: 'Aucun membre',
        description: 'Personne n’a encore de rôle sur ce projet.',
      },
    },
  },
  data: {
    common: {
      yes: 'Oui',
      no: 'Non',
      unknownDetail: 'erreur inconnue',
      identifier: 'Identifiant',
      description: 'Description',
      labels: 'Étiquettes',
      created: 'Créé',
      updated: 'Modifié',
      owner: 'Propriétaire',
      saved: 'Modifications enregistrées.',
    },
    buckets: {
      title: 'Buckets',
      subtitle: 'Le stockage objet déclaré à l’organisation {{tenant}} : là où les jobs lisent et écrivent.',
      searchPlaceholder: 'Rechercher un bucket…',
      columns: {
        name: 'Nom',
        type: 'Type',
        location: 'Emplacement',
        description: 'Description',
        updated: 'Modifié',
      },
      types: {
        s3: 'S3',
        managed: 'Managé',
        azure: 'Azure',
        gcp: 'GCP',
        generic: 'Générique',
        unknown: 'Autre',
      },
      filters: { type: 'Type' },
      empty: {
        title: 'Aucun bucket',
        description: 'Aucun bucket n’est déclaré pour cette organisation.',
      },
      detail: {
        subtitle: 'Bucket {{type}} · {{location}}',
        tabs: { files: 'Fichiers', summary: 'Résumé' },
        location: 'Emplacement',
        endpoint: 'Point d’accès',
        region: 'Région',
        infrastructure: 'Infrastructure',
        scope: 'Portée',
        locked: 'Verrouillé',
        credentialsNote:
          'Les identifiants d’accès sont conservés dans le magasin de secrets de l’organisation ; la console ne les affiche jamais.',
      },
      files: {
        root: 'Racine',
        breadcrumb: 'Emplacement dans le bucket',
        parent: 'Dossier parent',
        title: 'Contenu',
        columns: { name: 'Nom', size: 'Taille', type: 'Type de contenu', modified: 'Modifié' },
        searchPlaceholder: 'Rechercher dans ce dossier…',
        upload: 'Déposer des fichiers',
        uploadInput: 'Fichiers à déposer',
        uploaded_one: '{{count}} fichier déposé dans {{path}}.',
        uploaded_other: '{{count}} fichiers déposés dans {{path}}.',
        uploadFailed: 'Le dépôt a échoué : {{detail}}',
        download: 'Télécharger {{name}}',
        downloadFailed: 'Le téléchargement a échoué : {{detail}}',
        delete: 'Supprimer {{name}}',
        deleteTitle: 'Supprimer le fichier',
        confirmDelete: 'Supprimer « {{path}} » ? L’objet est effacé du bucket, sans corbeille.',
        deleted: '« {{name}} » supprimé.',
        deleteFailed: 'La suppression a échoué : {{detail}}',
        truncated:
          'Ce dossier compte au moins {{count}} entrées : le serveur n’en rend pas davantage, l’affichage peut être incomplet.',
        empty: { title: 'Dossier vide', description: 'Déposez un premier fichier dans ce dossier.' },
      },
    },
    catalog: {
      title: 'Catalogue',
      subtitle: 'Les données de l’organisation {{tenant}}, rangées en catalogues, bases, tables et champs.',
      levels: { layer: 'Catalogue', database: 'Base', table: 'Table', field: 'Champ' },
      children: { layer: 'Bases', database: 'Tables', table: 'Champs' },
      columns: {
        name: 'Nom',
        technicalName: 'Nom technique',
        description: 'Description',
        type: 'Type',
        format: 'Format',
        nullable: 'Nullable',
        location: 'Emplacement',
        updated: 'Modifié',
      },
      search: {
        layer: 'Rechercher un catalogue…',
        database: 'Rechercher une base…',
        table: 'Rechercher une table…',
        field: 'Rechercher un champ…',
      },
      new: {
        layer: 'Nouveau catalogue',
        database: 'Nouvelle base',
        table: 'Nouvelle table',
        field: 'Nouveau champ',
      },
      newSubtitle: {
        layer:
          'Un catalogue regroupe des bases selon leur degré de préparation (raw, curated…) ou leur moteur (iceberg).',
        database: 'Une base regroupe les tables d’un domaine, dans le catalogue {{parent}}.',
        table: 'Une table décrit un jeu de données stocké, dans la base {{parent}}.',
        field: 'Un champ est une colonne de la table {{parent}}.',
      },
      empty: {
        layer: {
          title: 'Aucun catalogue',
          description: 'Créez un premier catalogue (raw, curated…) pour ranger les données.',
        },
        database: { title: 'Aucune base', description: 'Ce catalogue ne contient encore aucune base.' },
        table: { title: 'Aucune table', description: 'Cette base ne contient encore aucune table.' },
        field: { title: 'Aucun champ', description: 'Le schéma de cette table n’est pas encore déclaré.' },
      },
      created: '« {{name}} » ajouté au catalogue.',
      detail: {
        tabs: { summary: 'Résumé', definition: 'Définition' },
      },
      delete: {
        layer: 'Supprimer le catalogue',
        database: 'Supprimer la base',
        table: 'Supprimer la table',
        field: 'Supprimer le champ',
      },
      confirmDelete: {
        layer: 'Supprimer le catalogue « {{name}} » ? Ses bases, tables et champs sont supprimés avec lui.',
        database: 'Supprimer la base « {{name}} » ? Ses tables et leurs champs sont supprimés avec elle.',
        table: 'Supprimer la table « {{name}} » ? Ses champs sont supprimés avec elle.',
        field: 'Supprimer le champ « {{name}} » ?',
      },
      deleted: '« {{name}} » supprimé du catalogue.',
      deleteFailed: 'La suppression a échoué : {{detail}}',
      fields: {
        technicalName: 'Nom technique',
        technicalNameHelp:
          'Clé dans les URL et les moteurs SQL : minuscules, chiffres et « _ ». Il ne change plus ensuite.',
        name: 'Libellé',
        nameHelp: 'Le nom affiché ; le nom technique par défaut.',
        description: 'Description',
        uri: 'Emplacement',
        uriHelp: 'Préfixe de stockage de la table, par exemple s3://energie-demo/curated/conso/.',
        tableType: 'Type de table',
        tableTypeHelp: 'Tel que le moteur le déclare (file pour des fichiers sur le stockage objet).',
        format: 'Format',
        fieldType: 'Type',
        fieldTypeHelp: 'Type de la colonne, tel que le moteur l’écrit (string, double, int64, date32[day]…).',
        nullable: 'Valeurs nulles permises',
      },
    },
    warehouses: {
      title: 'Entrepôts SQL',
      subtitle: 'Les entrepôts SQL de l’organisation {{tenant}}.',
      searchPlaceholder: 'Rechercher un entrepôt…',
      columns: {
        name: 'Nom',
        engine: 'Moteur',
        status: 'État',
        version: 'Version',
        size: 'Taille',
        owner: 'Propriétaire',
        updated: 'Modifié',
      },
      engines: {
        trino: 'Trino',
        postgresql: 'PostgreSQL',
        mysql: 'MySQL',
        mariadb: 'MariaDB',
        oracle: 'Oracle',
        sqlserver: 'SQL Server',
        db2: 'IBM DB2',
        snowflake: 'Snowflake',
        unknown: 'Autre moteur',
      },
      kinds: { managed: 'Managé', external: 'Externe', unknown: 'Autre' },
      statuses: {
        running: 'En service',
        stopped: 'Arrêté',
        pending: 'Démarrage',
        starting: 'Démarrage',
        failed: 'En échec',
        unknown: 'Inconnu',
      },
      workers_one: '{{count}} worker',
      workers_other: '{{count}} workers',
      filters: { status: 'État' },
      empty: {
        title: 'Aucun entrepôt',
        description: 'Aucun entrepôt SQL n’est déclaré pour cette organisation.',
      },
      detail: {
        subtitle: '{{engine}} {{version}} · {{kind}}',
        tabs: { summary: 'Résumé', tables: 'Tables', changes: 'Migrations', logs: 'Journal', costs: 'Coûts' },
        engine: 'Moteur',
        kind: 'Mode',
        version: 'Version',
        instanceType: 'Type d’instance',
        workers: 'Workers',
        infrastructure: 'Infrastructure',
        identity: 'Identité d’exécution',
        publicUrl: 'URL',
        conf: 'Configuration',
        env: 'Variables d’environnement',
        credentialsNote:
          'Les identifiants de connexion de l’entrepôt ne sont jamais affichés par la console.',
        query: 'Requêter',
      },
      tables: {
        title: 'Tables exposées',
        description: 'Ce que le moteur de l’entrepôt voit, lu à la demande.',
        column: 'Table',
        searchPlaceholder: 'Rechercher une table…',
        empty: { title: 'Aucune table', description: 'Le moteur n’expose encore aucune table.' },
      },
      changes: {
        title: 'Migrations',
        description: 'Scripts de schéma appliqués à l’entrepôt, dans l’ordre.',
        columns: {
          order: 'Ordre',
          filename: 'Fichier',
          description: 'Description',
          author: 'Auteur',
          executed: 'Exécutée',
          tag: 'Étiquette',
        },
        searchPlaceholder: 'Rechercher une migration…',
        empty: { title: 'Aucune migration', description: 'Aucun script de schéma n’a encore été appliqué.' },
      },
      logs: { empty: 'Aucune ligne de journal pour cet entrepôt.' },
    },
    sql: {
      title: 'Requête SQL',
      subtitle:
        'Lecture seule sur l’entrepôt {{name}} : une instruction de lecture, {{limit}} lignes au plus.',
      scopeTenant:
        'Cet entrepôt Trino couvre tout le locataire : il lit toutes ses tables Iceberg, quel que soit le projet. Le droit de lecture du catalogue vient du rôle Catalog Reader.',
      fields: {
        sql: 'Requête',
        sqlHelp:
          'Une seule instruction SELECT, WITH, SHOW TABLES, DESCRIBE ou EXPLAIN ; les écritures sont refusées.',
        limit: 'Lignes au plus',
      },
      run: 'Exécuter',
      result: 'Résultat',
      resultMeta_one: '{{count}} ligne en {{ms}} ms',
      resultMeta_other: '{{count}} lignes en {{ms}} ms',
      truncated: 'Résultat coupé à {{limit}} lignes.',
      empty: { title: 'Aucune ligne', description: 'La requête ne renvoie aucune ligne.' },
      idle: { title: 'Aucune requête exécutée', description: 'Écrivez une requête, puis exécutez-la.' },
    },
  },
  sessions: {
    title: 'Sessions',
    subtitle:
      'Des noyaux Python interactifs : chaque cellule s’exécute dans un espace de travail, relayée par la plateforme.',
    searchPlaceholder: 'Rechercher une session…',
    columns: {
      name: 'Nom',
      kernel: 'Noyau',
      project: 'Projet',
      used: 'Dernière exécution',
      status: 'État',
      workspace: 'Espace de travail',
    },
    new: 'Nouvelle session',
    newTitle: 'Nouvelle session',
    newSubtitle:
      'Un noyau Python dans son propre espace de travail : compté dans le quota, arrêté après inactivité.',
    detailSubtitle: 'Noyau {{kernel}} — les variables survivent d’une cellule à l’autre.',
    empty: {
      title: 'Aucune session',
      description: 'Ouvrez une session pour explorer des données en Python, cellule par cellule.',
    },
    fields: {
      name: 'Nom',
      kernel: 'Noyau',
      kernelHelp: 'Vide : python3.',
      infrastructure: 'Infrastructure',
      instanceType: 'Taille',
      project: 'Projet',
      projectHelp:
        'Facultatif : les membres qui modifient le projet pourront aussi exécuter du code dans la session.',
      noProject: 'Aucun projet',
    },
    created: 'Session « {{name}} » créée : le noyau démarre.',
    delete: 'Supprimer la session',
    confirmDelete:
      'Supprimer la session « {{name}} » ? Son noyau et ses variables sont perdus ; les données des buckets et des tables restent.',
    deleted: 'Session « {{name}} » supprimée.',
    deleteFailed: 'La suppression a échoué : {{detail}}',
    cell: {
      title: 'Cellules',
      code: 'Code de la cellule',
      placeholder: 'import pandas as pd',
      input: 'Entrée [{{index}}]',
      run: 'Exécuter',
      hint: 'Ctrl+Entrée pour exécuter.',
      startingHint: 'Le noyau démarre : la première cellule peut attendre une minute.',
      starting: 'Le noyau démarre encore : réessayez dans quelques secondes.',
      failed: 'L’exécution a échoué : {{detail}}',
      noOutput: 'Aucune sortie.',
      meta: '{{status}} · exécution {{count}} · {{duration}} ms',
      kinds: { stdout: 'Sortie', stderr: 'Erreurs', result: 'Résultat', error: 'Exception' },
    },
  },
  ml: {
    common: {
      identifier: 'Identifiant',
      project: 'Projet',
      owner: 'Propriétaire',
      labels: 'Étiquettes',
      description: 'Description',
      created: 'Créé',
      updated: 'Modifié',
      saved: 'Modifications enregistrées.',
      unknownDetail: 'erreur inconnue',
    },
    fields: {
      name: 'Nom',
      nameHelp: {
        experiment: 'Le nom sous lequel le job d’entraînement consigne ses runs.',
        model: 'Le nom du modèle dans le registre de l’organisation.',
      },
      description: 'Description',
    },
    experiments: {
      title: 'Expériences',
      subtitle: 'Le suivi d’expériences du projet {{project}} : runs, paramètres et métriques.',
      searchPlaceholder: 'Rechercher une expérience…',
      columns: {
        name: 'Nom',
        description: 'Description',
        labels: 'Étiquettes',
        owner: 'Propriétaire',
        updated: 'Modifiée',
      },
      new: 'Nouvelle expérience',
      newTitle: 'Nouvelle expérience',
      newSubtitle: 'Une expérience regroupe les runs d’un même problème dans le projet {{project}}.',
      empty: {
        title: 'Aucune expérience',
        description: 'Créez une expérience, puis consignez-y les runs d’un job d’entraînement.',
      },
      created: 'Expérience « {{name}} » créée.',
      delete: 'Supprimer l’expérience',
      confirmDelete: 'Supprimer « {{name}} » ? Ses runs, leurs paramètres et leurs métriques sont effacés.',
      deleted: 'Expérience « {{name}} » supprimée.',
      deleteFailed: 'La suppression a échoué : {{detail}}',
      detail: {
        subtitle: 'Expérience du projet {{project}} · {{owner}}',
        tabs: { runs: 'Runs', summary: 'Résumé', definition: 'Définition', permissions: 'Permissions' },
        compare: 'Comparer les runs',
        trackedMetrics: 'Métriques suivies',
        trackedMetricsHint:
          'Les clés consignées par au moins un run de l’expérience, et leur sens quand il est connu.',
        noMetrics: 'Aucun run n’a encore consigné de métrique.',
        permissionsHint:
          'Les droits sur une expérience se donnent par affectation de rôle : un utilisateur, un groupe ou un agent reçoit un rôle sur cette expérience ou sur son projet.',
        grantRoles: 'Gérer les affectations',
      },
    },
    runs: {
      title: 'Runs',
      description:
        'Chaque run consigne ses paramètres et ses métriques. Ouvrez-en un, ou comparez-en jusqu’à trois.',
      searchPlaceholder: 'Rechercher un run…',
      columns: {
        name: 'Nom',
        status: 'Statut',
        description: 'Description',
        duration: 'Durée',
        start: 'Démarré',
      },
      filters: { status: 'Statut' },
      empty: {
        title: 'Aucun run',
        description:
          'Les runs apparaissent quand un job d’entraînement consigne ses paramètres et ses métriques.',
      },
      delete: 'Supprimer le run',
      confirmDelete: 'Supprimer le run « {{name}} » ? Ses paramètres et ses métriques sont effacés.',
      deleted: 'Run « {{name}} » supprimé.',
      deleteFailed: 'La suppression a échoué : {{detail}}',
      detail: {
        subtitle: 'Run de l’expérience {{experiment}}',
        tabs: { metrics: 'Métriques', params: 'Paramètres', summary: 'Résumé' },
        compare: 'Comparer',
        register: 'Enregistrer une version',
        registerTo: 'Dans le modèle…',
        noModel: 'Aucun modèle dans le registre',
        experiment: 'Expérience',
        status: 'Statut consigné',
        duration: 'Durée',
        start: 'Début',
        end: 'Fin',
        artifactUri: 'Emplacement des artefacts',
        jobRun: 'Run de job',
        noJobRun: 'Aucun : run journalisé hors d’un job graal.',
      },
      metrics: {
        empty: {
          title: 'Aucune métrique',
          description: 'Ce run n’a consigné aucune métrique.',
          running: 'Ce run est en cours : ses métriques apparaîtront au fil de l’entraînement.',
        },
        allValues: 'Toutes les valeurs',
        allValuesDescription: 'Chaque point consigné, par métrique et par pas.',
        searchPlaceholder: 'Rechercher une métrique…',
        columns: { key: 'Métrique', step: 'Pas', value: 'Valeur', time: 'Consigné le' },
      },
      params: {
        title: 'Paramètres',
        description: 'Les hyperparamètres consignés par le run, tels quels.',
        searchPlaceholder: 'Rechercher un paramètre…',
        columns: { key: 'Paramètre', value: 'Valeur' },
        empty: { title: 'Aucun paramètre', description: 'Ce run n’a consigné aucun paramètre.' },
      },
    },
    compare: {
      crumb: 'Comparaison',
      title: 'Comparer les runs',
      subtitle: 'Expérience {{experiment}} : {{max}} runs au plus, côte à côte.',
      picker: 'Runs comparés',
      pickerHint:
        '{{max}} runs au plus. Chacun garde sa couleur et sa forme de repère, dans les courbes comme dans la légende.',
      none: { title: 'Aucun run choisi', description: 'Choisissez au moins un run ci-dessus.' },
      best: 'meilleur',
      direction: { min: 'plus bas = meilleur', max: 'plus haut = meilleur' },
      metrics: {
        title: 'Métriques',
        description:
          'La dernière valeur de chaque métrique. Le meilleur run est marqué quand le sens de la métrique est connu.',
        searchPlaceholder: 'Rechercher une métrique…',
        columns: { key: 'Métrique' },
        empty: { title: 'Aucune métrique', description: 'Les runs choisis n’ont consigné aucune métrique.' },
      },
      charts: {
        title: 'Courbes',
        description:
          'Une métrique par graphique. Survolez, ou utilisez les flèches du clavier, pour lire la valeur de chaque run à un pas.',
      },
      params: {
        title: 'Paramètres',
        description: 'Les hyperparamètres des runs choisis. Filtrez sur les valeurs qui diffèrent.',
        searchPlaceholder: 'Rechercher un paramètre…',
        columns: { key: 'Paramètre' },
        filter: 'Valeurs',
        different: 'Différentes',
        same: 'Identiques',
        empty: { title: 'Aucun paramètre', description: 'Les runs choisis n’ont consigné aucun paramètre.' },
      },
    },
    charts: {
      linesCaption: 'par pas d’entraînement',
      barsCaption: 'valeur consignée',
      legend: 'Légende',
      step: 'pas {{step}}',
      noPoints: 'Aucun point consigné.',
      summary: '{{metric}}, dernière valeur par run : {{values}}.',
    },
    models: {
      title: 'Modèles',
      subtitle: 'Le registre de modèles de l’organisation {{tenant}} : versions et étapes.',
      searchPlaceholder: 'Rechercher un modèle…',
      columns: { name: 'Nom', description: 'Description', labels: 'Étiquettes', updated: 'Modifié' },
      new: 'Nouveau modèle',
      newTitle: 'Nouveau modèle',
      newSubtitle: 'Un modèle regroupe les versions successives d’un même prédicteur ; chacune a son étape.',
      fields: {
        project: 'Projet',
        projectHelp:
          'Le projet propriétaire : ses membres qui modifient ses expériences gèrent ce modèle. Il ne change plus ensuite.',
      },
      empty: {
        title: 'Aucun modèle',
        description: 'Enregistrez un modèle, puis ses versions depuis les runs de vos expériences.',
      },
      created: 'Modèle « {{name}} » créé.',
      delete: 'Supprimer le modèle',
      confirmDelete: 'Supprimer le modèle « {{name}} » et toutes ses versions ? Les artefacts sont effacés.',
      deleted: 'Modèle « {{name}} » supprimé.',
      deleteFailed: 'La suppression a échoué : {{detail}}',
      detail: {
        subtitle: 'Modèle du registre de {{tenant}}',
        tabs: { versions: 'Versions', summary: 'Résumé', definition: 'Définition' },
        production: 'Version {{version}} en production',
        noProduction: 'Aucune version en production',
        productionVersion: 'Version en production',
        versionCount: 'Versions',
      },
    },
    versions: {
      title: 'Versions',
      description:
        'Chaque version pointe vers un artefact et, quand elle en vient, vers son run d’expérience.',
      label: 'v{{name}}',
      searchPlaceholder: 'Rechercher une version…',
      columns: {
        name: 'Version',
        stage: 'Étape',
        run: 'Run d’origine',
        description: 'Description',
        created: 'Créée',
      },
      filters: { stage: 'Étape' },
      empty: {
        title: 'Aucune version',
        description: 'Créez une version, ou enregistrez-en une depuis le détail d’un run.',
      },
      new: 'Nouvelle version',
      newTitle: 'Nouvelle version de {{model}}',
      newSubtitle: 'La version naît à l’étape « Aucune » ; changez-la ensuite depuis son détail.',
      fromRun: 'Depuis le run {{run}} de l’expérience {{experiment}}.',
      fields: {
        name: 'Numéro de version',
        nameHelp: 'Lettres, chiffres, « . », « _ » et « - ». Par défaut, le numéro suivant.',
        source: 'Source de l’artefact',
        sourceHelp: 'URI de l’artefact du modèle (s3://…, mlflow_artifact:/…). Facultatif.',
      },
      created: 'Version {{name}} créée.',
      delete: 'Supprimer la version',
      confirmDelete: 'Supprimer la version {{name}} du modèle {{model}} ? Son artefact est effacé.',
      deleted: 'Version {{name}} supprimée.',
      deleteFailed: 'La suppression a échoué : {{detail}}',
      detail: {
        subtitle: 'Version du modèle {{model}}',
        tabs: { summary: 'Résumé', artifact: 'Artefact' },
        run: 'Run d’origine',
        noRun: 'Aucun run d’origine',
        experiment: 'Expérience d’origine',
        source: 'Source',
      },
      artifact: {
        filename: 'Fichier',
        size: 'Taille',
        contentType: 'Type de contenu',
        key: 'Clé de stockage',
        lastModified: 'Modifié',
        download: 'Télécharger {{name}}',
        downloadFailed: 'Le téléchargement a échoué : {{detail}}',
      },
    },
    stages: {
      field: 'Étape',
      values: { None: 'Aucune', Staging: 'Préproduction', Production: 'Production', Archived: 'Archivée' },
      change: 'Changer d’étape',
      changeTitle: 'Changer l’étape de la version {{name}}',
      changeDescription: 'L’étape dit à quoi sert la version : essai, préproduction, production, archive.',
      archiveExisting: 'Archiver les autres versions en production',
      archiveExistingHelp: 'Le modèle n’a alors qu’une version en production.',
      submit: 'Changer l’étape',
      changed: 'Version {{name}} : {{stage}}.',
      archived_one: '{{count}} autre version archivée.',
      archived_other: '{{count}} autres versions archivées.',
    },
  },
  // Lot L5 — administration et exécution : textes dans `admin.fr.ts`.
  admin: adminFr,
  secretStore: secretStoreFr,
  // 2026-10-06 — volet social (#52) et galerie de gabarits low-code (#54) : `social.fr.ts`.
  // Lot L08 — agents gouvernés et IA souveraine (ADR 0080) : `ai.fr.ts`.
  ai: aiFr,
  social: socialFr,
  // 2026-10-10 — lot L01, facturation (factures, transactions, moyens de paiement) : `billing.fr.ts`.
  billing: billingFr,
  gallery: galleryFr,
  // 2026-10-10 — recherche globale (ADR 0043) : `search.fr.ts`.
  search: searchFr,
  // 2026-10-10 — usage CPU et mémoire (ADR 0053) : `usage.fr.ts`.
  resourceUsage: resourceUsageFr,
  // 2026-10-10 — orchestration (reprises, rattrapage, backfills, déclencheurs) : `orchestration.fr.ts`.
  orchestration: orchestrationFr,
  // 2026-10-19 — alertes d'un job (webhooks signés, derniers envois) : `alerts.fr.ts`.
  alerts: alertsFr,
  // Lot L07 — connexions, lignage, qualité, aperçu par étape, recherche du catalogue : `donnees.fr.ts`.
  connections: connectionsFr,
  lineage: lineageFr,
  quality: qualityFr,
  preview: previewFr,
  catalogSearch: catalogSearchFr,
  catalogTable: catalogTableFr,
  glossary: glossaryFr,
  // 2026-10-23 — écarts avec la console Angular (réglages et métriques d'un projet, buckets,
  // entrepôts, préférences) : `gestes.fr.ts`.
  projectSettings: projectSettingsFr,
  projectMetrics: projectMetricsFr,
  bucketManage: bucketManageFr,
  warehouseManage: warehouseManageFr,
  managedWarehouse: managedWarehouseFr,
  preferences: preferencesFr,
  // 2026-10-30 — aperçu d'un fichier (buckets, librairies) : `apercu-fichier.fr.ts`.
  filePreview: filePreviewFr,
} as const;
